/**
 * NetExposure.tsx — Net Exposure page
 *
 * This page is PURE WEBSOCKET. REST is used only for one-shot bootstrap on
 * mount and on LP change; every subsequent position and price update arrives
 * via WebSocket.
 *
 * Exposure grid data sources:
 *   B-Book rows:        REST bootstrap via mt5Api, then /ws/v1/mt5/events.
 *                       Initial SNAPSHOT frame is a full replacement; subsequent
 *                       POSITION_* events upsert/remove in place.
 *   Coverage Book rows: REST bootstrap at GET /api/v1/fix/positions/{lp_id}
 *                       (v3 flat path — same endpoint CBookPage uses; the
 *                       nested /fix/lp/{id}/positions legacy path does not
 *                       return the full operational set). Combines Terminal +
 *                       DOM Trader + Hedging Strategies into a single LP-side
 *                       net per symbol. Subsequent updates arrive as
 *                       POSITION_REPORT / POSITION_CLOSED / POSITION_UPDATED
 *                       on the shared FIX WebSocket. Closed-position tombstones
 *                       from CBookPage (localStorage 'nexrisk_closed_positions')
 *                       are honoured so just-closed positions don't ghost back.
 *   Live prices:        MD depth:1 subscribed for every symbol with an open
 *                       position. FIX WS feeds currentPricesRef; the exposure
 *                       aggregation re-runs on every tick so Mkt Px and Broker
 *                       P/L update in real time. Symbol-group rows sum child
 *                       values (aggFunc:'sum') — hedged symbols net to zero.
 *   Sign convention:    Net Vol as the user sees it on screen.
 *                       B-Book row        = broker's side of the internal book
 *                                           (inverse of client direction).
 *                       Coverage Book row = broker's actual LP position direction.
 *                       On a fully hedged symbol the two rows are equal in
 *                       magnitude, opposite in sign, and the symbol-group
 *                       parent row (aggFunc:'sum') reads zero.
 *
 * DOM Trader: same FIX WS stream, plus a depth-10 MD subscription for the
 *             selected DOM symbol driving the book-depth panel.
 *   Order entry:  POST /api/v1/fix/order — fill confirmation arrives as
 *                 POSITION_REPORT; no explicit refresh needed.
 *
 * Platforms (Books cTrader 1.4). One book across MT5 and cTrader; only the
 * MASTER of each platform counts. Legs under each symbol:
 *   B-Book · MT5        the MT5 MASTER's clients (mt5.position), broker view.
 *   B-Book · cTrader    the cTrader MASTER's clients, one leg per cTrader book
 *                       (ctrader.position on the same socket), broker view,
 *                       money from profit_usd. Book A clients are the broker's
 *                       clients too; their LP side is the cTrader A-Book leg.
 *   Coverage · <LP>     Taiga's LP positions (Strategy, DOM Trader, Terminal).
 *                       One LP position can cover both platforms' flow.
 *   Coverage · cTrader  the cTrader MASTER's book A positions as the LP
 *   A-Book              position (at cTrader's own LP), same side as the client.
 * Platform filter (All / MT5 / cTrader) replaces the MT5 node dropdown. Under
 * MT5 or cTrader the Taiga-LP leg stays shown, greyed and tagged "All
 * platforms", with the same values as under All, and enters no sum: the
 * symbol's Net, Hedge Ratio, Hedge Impact and Status show only under All.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/stores/AuthContext';
import {
  mt5Api, isCTraderFrame, CTRADER_POSITION_TOPIC,
  connectPortfolioExposureWebSocket, getPortfolioExposureSymbols,
  type PortfolioExposureSymbolRow, type PortfolioExposureSymbolsData,
  type MT5PositionWithNode, type MT5NodeAPI, type CTraderPositionMessage, type CTraderPositionRow,
} from '@/services/api';
import { usePlatformMasters } from '@/hooks/usePlatformMasters';
import { useListedLps } from '@/hooks/useListedLps';
import { useCTraderMasterPositions, ctProfitUsd, ctUsdRate } from '@/hooks/useCTraderMasterPositions';
import { hedgeState, LOT_EPS, type HedgeStatus } from './netExposure/riskNote';
import { RiskNoteDrawer, type RiskNoteRequest, type RiskNoteSnapshot } from './netExposure/RiskNoteDrawer';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import type {
  ColDef,
  RowClickedEvent,
  GridReadyEvent,
  RowGroupOpenedEvent,
  FirstDataRenderedEvent,
  GridSizeChangedEvent,
  ColumnVisibleEvent,
  ColumnResizedEvent,
  ColumnRowGroupChangedEvent,
} from 'ag-grid-community';
import { themeQuartz } from 'ag-grid-community';
import { clsx } from 'clsx';
import { conversionSymbolOf, toUsd } from '@/lib/pnlUsd';
import { domOrderRules, unitsPerPositionQty, qtyProblem } from './domOrderRules';

// ======================
// CONSTANTS / FETCH
// ======================
const API_BASE = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';
const WS_BASE  = (import.meta as any).env?.VITE_WS_URL  || 'ws://localhost:8080';
const FIX_WS_PATH = '/ws/v1/fix/events';

async function bff<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error((err as any).error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ======================
// THEME — matches BBookPage (the definitive styling reference).
// No page-wide fontFamily override; Quartz's default sans (Inter) keeps
// column headers out of monospace. Per-cell numeric formatting uses the
// 'font-mono' Tailwind class in column defs below.
// ======================
const gridTheme = themeQuartz.withParams({
  backgroundColor: '#232326',
  browserColorScheme: 'dark',
  chromeBackgroundColor: { ref: 'foregroundColor', mix: 0.11, onto: 'backgroundColor' },
  fontSize: 14,
  foregroundColor: '#FFF',
  headerFontSize: 14,
});

// ======================
// TYPES
// ======================
interface HedgeExposureRow {
  id: string;
  // Display symbol — what the leaf row's Symbol cell shows. For B-Book leaves,
  // this is the broker-side mt5_symbol (e.g. 'Gold' on Highness MT5). For
  // Coverage leaves, this is the LP-side lp_symbol (e.g. 'XAUUSD' on TE) —
  // the leaf row identifies the venue-specific instrument as it actually
  // exists on that venue's books.
  symbol: string;
  // Grouping key — drives AG Grid's row grouping so that B-Book and Coverage
  // legs of the same logical instrument fall under one parent. For B-Book
  // this is the same as `symbol`. For Coverage with an active LP mapping
  // (e.g. TE 'XAUUSD' → MT5 'Gold'), this is the mapped mt5_symbol; for
  // Coverage with no mapping it equals `symbol` as a passthrough.
  groupSymbol: string;
  lp: string;
  lpAccount: string;
  clientNetVol: number;
  hedgeNetVol: number;
  brokerNetVol: number;
  clientNetNotional: number;
  hedgeNetNotional: number;
  brokerNetNotional: number;
  avgPrice: number;
  brokerFloatingPL: number;
  unhedgedLots: number;
  breakEvenPrice: number;
  probableIdp30: 'Up' | 'Down' | 'Neutral';
  bevh: number;
  riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
  marketMovePercent: number;
  plImpact: number;
  isBBook: boolean;
  /** Where the leg comes from. LP = Taiga's LP positions, which cover both platforms. */
  source: LegSource;
  /** cTrader legs: positions left out of Broker P/L (no price or no rate to USD). */
  unpriced?: number;
  /** cTrader legs while the cTrader MASTER is not connected: last known, no prices. */
  stale?: boolean;
  /** Taiga LP legs: the LP's id (prices are kept per LP). */
  lpId?: string;
  /** Unifier the leg's symbol belongs to (outer grouping level); undefined
   *  for a stand-alone symbol, which then groups at the top level. */
  unifierKey?: string;
}

type LegSource = 'MT5' | 'CTRADER' | 'LP';
type PlatformFilter = 'All' | 'MT5' | 'cTrader';

interface FIXPosition {
  position_id: string;
  account: string;
  symbol: string;
  open_price: number;
  long_qty: number;
  short_qty: number;
  net_qty: number;
  side: string;
  commission: number;
  swap: number;
  received_ts: number;
  unrealized_pnl?: number;
  current_price?: number;
  /** LMAX: currency units per contract (EUR/USD 10,000). Absent: quantities are units. */
  contract_size?: number;
  /** Bridge's normalised name across LPs (CMC 'EUR/USD' → 'EURUSD'). */
  canonical_symbol?: string;
  /** Netted LP (FIX Bridge v2.1): quantities are LP units, not contracts. */
  netted?: { a_qty: number; a_avg_px: number; c_qty: number; c_avg_px: number; untracked_qty: number };
  /** Page-side: the LP the position is at. */
  _lp?: string;
}

// Open LP position. A netted position (FIX Bridge v2.1) reports open_price 0
// when part of it is untracked, and is still open. Same rule as Coverage.
const isOpenLpPosition = (p: FIXPosition): boolean =>
  !!p.position_id && (p.open_price > 0 || !!p.netted);

// A netted LP keeps one position id per symbol and reuses it when the position
// reopens. One the bridge returns holding a quantity is open, whatever was closed
// under that id before, so a closed-position tombstone never hides it.
const nettedHeld = (p: FIXPosition): boolean => !!p.netted && !!(
  Number(p.netted.a_qty) || Number(p.netted.c_qty) || Number(p.netted.untracked_qty));

// A netted position's signed size in LP units: the sum of its parts.
// Its top-level side and quantities are not relied on.
const nettedNet = (p: FIXPosition): number | null => p.netted
  ? (Number(p.netted.a_qty) || 0) + (Number(p.netted.c_qty) || 0) + (Number(p.netted.untracked_qty) || 0)
  : null;

// The priced part of an LP position, for break-even and P/L.
// Netted: Strategy + DOM parts at their own prices; the untracked part has no
// price, so it counts in volume but not here. Others: the whole position.
function lpPricedPart(p: FIXPosition, lots: number, lotSize: number): { px: number; lots: number } {
  const n = p.netted;
  if (!n) return { px: p.open_price, lots };
  const a = Math.abs(Number(n.a_qty) || 0), c = Math.abs(Number(n.c_qty) || 0);
  if (a + c === 0) return { px: 0, lots: 0 };
  const px = (a * (Number(n.a_avg_px) || 0) + c * (Number(n.c_avg_px) || 0)) / (a + c);
  return { px, lots: (a + c) * (p.contract_size || 1) / lotSize };
}

interface FIXLpEntry {
  lp_id: string;
  lp_name: string;
  state: string;
  provider_type: string;
}

// Subset of the LP-mapping row shape served by GET /api/v1/symbol-mappings
// (the BFF route that proxies to C++ /api/v1/mappings/lp). Mirrors
// SymbolMapping.tsx's LPMapping interface for the fields this page needs:
// the join key (lp_id, lp_symbol) and the canonical mt5_symbol it points
// to, plus `enabled` so disabled rows don't influence grouping.
//
// Note: SymbolMapping.tsx's interface declares an `approved: boolean` but
// the C++ API does not currently emit that field. Filtering on `enabled`
// alone matches what the backend exposes.
interface LPMapping {
  id:           number;
  mt5_symbol:   string;
  lp_id:        string;
  lp_name:      string;
  lp_symbol:    string;
  enabled:      boolean;
}

// Backend-sourced Coverage-side daily aggregates (same endpoint CBookPage uses).
// realized_pnl persists across the day even when all positions close — that's
// exactly the property the header bar needs.
interface DailyStats {
  trade_date:     string;
  realized_pnl:   number;
  commission:     number;
  swap_long:      number | null;
  swap_short:     number | null;
  swap_net:       number;
  position_count: number;
}

// Client-side fallback for B-Book realised P/L. The MT5 backend doesn't expose
// a daily-stats equivalent yet, so we accumulate closed-position P/L into this
// structure on every POSITION_DELETE event, using the last-known P/L snapshot
// captured just before the delete. Persisted to localStorage keyed by trade_date
// so the header bar survives page refreshes within a trading day. Clears on
// rollover (detected via DailyStats.trade_date advancing).
interface BBookDayStats {
  trade_date:     string;
  realized_pnl:   number;
  closed_count:   number;
}

interface FIXInstrument {
  symbol: string;
  security_id: string;
  currency: string;
  description: string;
  instrument_group: string;
  min_trade_vol: number;
  max_trade_vol: number;
  price_precision: number;
  has_trade_route: boolean;
  trade_route: string;
}

interface FIXCapabilities {
  lp_id: string;
  order_types: string[];
  time_in_force: string[];
  max_order_qty: number;
  min_order_qty: number;
  custom_fields: { sl_tp?: boolean; product_type?: boolean; open_close?: boolean };
}

interface FIXLpStatus {
  lp_id: string;
  state: string;
  trading_session: { state: string; active_orders: number; positions_loaded: number; instruments_loaded: number };
  md_session: { state: string };
}

interface BookLevel { price: number; size: number; }

interface BookData {
  symbol: string;
  best_bid: number | null;
  best_ask: number | null;
  spread: number | null;
  bids: BookLevel[];
  asks: BookLevel[];
  last_update_ts: number;
}

interface FIXOrderResp {
  success: boolean; clord_id?: string; error?: string;
  /** Unifier pointer kept on the order, or null (FIX Bridge, DOM Trader pointer). */
  pointer?: { platform: string; symbol: string } | null;
  /** Non-blocking: POINTER_IGNORED when an invalid point_to was dropped. */
  warnings?: { code: string; message: string }[];
}

/** A unifier member an LP symbol can be pointed to (dom-targets). */
interface DomPointTarget { symbol: string; platform: string; unifier: string }
const pointKey = (t: { platform: string; symbol: string }) => `${t.platform}|${t.symbol}`;
// Last "Point to" choice per LP symbol, per browser. Convenience only.
const DOM_POINT_STORE = 'nexrisk_dom_point_to';
function readPointStore(): Record<string, string> {
  try { return JSON.parse(localStorage.getItem(DOM_POINT_STORE) ?? '{}') ?? {}; } catch { return {}; }
}
function writePointStore(lpSymKey: string, choice: string): void {
  try {
    const m = readPointStore();
    if (choice) m[lpSymKey] = choice; else delete m[lpSymKey];
    localStorage.setItem(DOM_POINT_STORE, JSON.stringify(m));
  } catch { /* storage unavailable: choice is not remembered */ }
}

interface ExecEntry {
  clord_id: string;
  ts: number;
  symbol: string;
  side: 'BUY' | 'SELL';
  qty: number;
  orderType: string;
  tif: string;
  lpId: string;
  status: 'SENT' | 'REJECTED';
  rejectReason?: string;
  /** Member the order was pointed to, as kept by the backend. */
  pointedTo?: string;
}

// ======================
// SEED / CONSTANTS
// ======================
const SEED_LPS: FIXLpEntry[] = [
  { lp_id: 'traderevolution', lp_name: 'TraderEvolution', state: 'CONNECTED', provider_type: 'traderevolution' },
];

const DOM_DEPTH = 5;

// ── Big-figure price (mirrors FxAskCell typography) ──────────────────────────
// handle (white, smaller) · pip pair (accent, large) · pipette (accent, sup).
// Precision-aware: >=3 → handle|pip|pipette; ==2 → handle|pip; <2 → handle only.
// Used in the DOM Trader Market Depth panel so the sensitive (last) digits read
// the same way they do in the reserved FX cells.
function BigFigurePrice({
  price, precision, accent, handlePx, pipPx, pipettePx,
}: {
  price: number; precision: number; accent: string;
  handlePx: number; pipPx: number; pipettePx: number;
}) {
  const fixed = price.toFixed(Math.max(0, precision));
  let handle = fixed, pip = '', pipette: string | null = null;
  if (precision >= 3)       { handle = fixed.slice(0, -3); pip = fixed.slice(-3, -1); pipette = fixed.slice(-1); }
  else if (precision === 2) { handle = fixed.slice(0, -2); pip = fixed.slice(-2); }
  return (
    <span className="font-mono" style={{ lineHeight: 1, whiteSpace: 'nowrap' }}>
      <span style={{ color: '#fff', fontSize: handlePx }}>{handle}</span>
      {pip && <span style={{ color: accent, fontSize: pipPx, fontWeight: 600 }}>{pip}</span>}
      {pipette && <sup style={{ color: accent, fontSize: pipettePx, fontWeight: 600, marginLeft: 1 }}>{pipette}</sup>}
    </span>
  );
}

// ======================
// BOOK HELPERS — exact copy from CBookPage
// ======================
function buildBookFromMaps(
  symbol: string,
  bidsMap: Map<number, number>,
  asksMap: Map<number, number>,
): BookData | null {
  const bids: BookLevel[] = [...bidsMap.entries()]
    .map(([price, size]) => ({ price, size }))
    .sort((a, b) => b.price - a.price)
    .slice(0, DOM_DEPTH);
  const asks: BookLevel[] = [...asksMap.entries()]
    .map(([price, size]) => ({ price, size }))
    .sort((a, b) => a.price - b.price)
    .slice(0, DOM_DEPTH);
  const bestBid = bids[0]?.price ?? null;
  const bestAsk = asks[0]?.price ?? null;
  if (bestBid == null || bestAsk == null) return null;
  return { symbol, best_bid: bestBid, best_ask: bestAsk, spread: +(bestAsk - bestBid).toFixed(5), bids, asks, last_update_ts: Date.now() };
}

function primeBookFromRest(data: any, bidsMap: Map<number, number>, asksMap: Map<number, number>): void {
  if (!data) return;
  let rawBids: any[] = data.bids || [];
  let rawAsks: any[] = data.asks || [];
  const refBid: number | null = data.best_bid ?? null;
  const refAsk: number | null = data.best_ask ?? null;
  if (refBid != null && refAsk != null && refBid > refAsk) [rawBids, rawAsks] = [rawAsks, rawBids];
  bidsMap.clear(); asksMap.clear();
  for (const b of rawBids) if (b.price && b.size) bidsMap.set(Number(b.price), Number(b.size));
  for (const a of rawAsks) if (a.price && a.size) asksMap.set(Number(a.price), Number(a.size));
}

function applyBookMessage(data: any, type: string, bidsMap: Map<number, number>, asksMap: Map<number, number>): boolean {
  if (!data) return false;
  const isSnapshot    = type === 'MARKET_DATA_SNAPSHOT' || type === 'MD_SNAPSHOT';
  const isIncremental = type === 'MARKET_DATA_INCREMENTAL' || type === 'MD_INCREMENTAL';
  if (!isSnapshot && !isIncremental) return false;
  if (isSnapshot) { primeBookFromRest(data, bidsMap, asksMap); return bidsMap.size > 0 || asksMap.size > 0; }
  if (Array.isArray(data.entries) && data.entries.length > 0) {
    for (const e of data.entries) {
      const map = (e.entry_type === 'BID') ? bidsMap : asksMap;
      if (e.action === 'DELETE' || e.size === 0) map.delete(Number(e.price));
      else map.set(Number(e.price), Number(e.size));
    }
    return true;
  }
  let rawBids: any[] = data.bids || [];
  let rawAsks: any[] = data.asks || [];
  const refBid: number | null = data.best_bid ?? null;
  const refAsk: number | null = data.best_ask ?? null;
  if (refBid != null && refAsk != null && refBid > refAsk) [rawBids, rawAsks] = [rawAsks, rawBids];
  bidsMap.clear(); asksMap.clear();
  let mutated = false;
  for (const b of rawBids) if (b.price != null && b.size != null) { bidsMap.set(Number(b.price), Number(b.size)); mutated = true; }
  for (const a of rawAsks) if (a.price != null && a.size != null) { asksMap.set(Number(a.price), Number(a.size)); mutated = true; }
  return mutated;
}

// ======================
// B-BOOK AGGREGATOR
// ======================
// Class flags for an MT5 symbol — drives lotSize/pipValue lookup. Built from
// LP mappings: a broker-specific name like 'Gold' on the MT5 server can be
// resolved to 'XAUUSD' (its lp_symbol counterpart) which we then classify on
// .includes('XAU'). Without this lookup, 'Gold'.includes('XAU') is false and
// the symbol falls into the FX-default branch (lotSize=100,000), so 1.06 lots
// of Gold renders as 106,000 units instead of the correct 106 oz.
type Mt5SymbolClass = { isJPY: boolean; isXAU: boolean; isBTC: boolean };

// Units per MT5 lot, as this page sizes every leg (MT5 lots are the unit of
// the symbol group). Class from the LP mappings first, else the symbol name.
function symLotSize(sym: string, mt5SymbolClass?: Map<string, Mt5SymbolClass>): number {
  const cls = mt5SymbolClass?.get(sym);
  const isXAU = cls?.isXAU ?? sym.includes('XAU');
  const isBTC = cls?.isBTC ?? sym.includes('BTC');
  return isXAU ? 100 : isBTC ? 1 : 100000;
}

// Grid legs are built from the exposure topic (legsFromExposure below), not
// from positions: an LP position fanned out to every mapped symbol counted
// once per mapped symbol. Positions feed the header readouts and the DOM only.

// cTrader money of one position, USD, for the header readouts.
//   clientPnl: the client's floating profit (broker view is its negative).
//   charges:   swap + commission at usd_rate, the B-Book convention (as MT5).
function ctMoney(r: CTraderPositionRow): { clientPnl: number; charges: number } {
  const rate = ctUsdRate(r);
  return {
    clientPnl: ctProfitUsd(r) ?? 0,
    charges:   rate === null ? 0 : (r.swap + r.commission) * rate,
  };
}

// ======================
// LEGS FROM THE EXPOSURE TOPIC (Unifier Symbols v1.1, sections 2-3)
// Every symbol figure on the grid comes from portfolio.exposure.symbols. The
// member split (hedges per member, a netted LP position split by member,
// pointed DOM trades) exists only in those rows; building legs from positions
// and fanning an LP position out to every mapped symbol counted it once per
// mapped symbol. Tree: unifier (by_unifier) > member (by_symbol.unifier) >
// legs; a stand-alone symbol is symbol > legs. Legs of a symbol:
//   B-Book · MT5       b_book_lots - b_book_lots_ctrader, broker view
//   B-Book · cTrader   b_book_lots_ctrader
//   Coverage · <LP>    coverage_by_lp, A + C per LP (source LP); the cTrader
//                      A-Book is its CTRADER entry. Adds up to the row's A + C.
// Group sums are the legs' sums; by_unifier is never added into a total.
// ======================

/** One coverage_by_lp entry: A and C of one LP in one row, broker view. */
interface CoverageByLp {
  lp_id:   string;
  source:  'LP' | 'CTRADER' | string;
  a_lots:  number;
  c_lots:  number;
  // Floating P&L, USD, broker view (Books 1.8, Coverage per LP).
  a_unrealized_usd?: number | null;
  c_unrealized_usd?: number | null;
}

type ExposureRowWithCover = PortfolioExposureSymbolRow & { coverage_by_lp?: CoverageByLp[] };

interface TopicLegCtx {
  /** 'B-Book-<MT5 master>' — the label the MT5 leg has always had. */
  bLabel:       string;
  ctMasterName: string | null;
  ctLive:       boolean;
  lpName:       (lpId: string) => string;
  /** The LP's own symbol for a platform symbol at that LP (LP mapping). */
  lpSymbolOf:   (lpId: string, symbol: string) => string | undefined;
  /** Trading account of the LP, from its open positions. */
  lpAccountOf:  (lpId: string) => string;
  lotSizeOf:    (symbol: string) => number;
}

const r2 = (v: number) => Math.round(v * 100) / 100;
const r5 = (v: number) => Math.round(v * 100000) / 100000;

/** The coverage P&L of one entry, or null when either part is not priced. */
function coverPnl(e: CoverageByLp): number | null {
  const a = Number(e.a_lots) ? e.a_unrealized_usd : 0;
  const c = Number(e.c_lots) ? e.c_unrealized_usd : 0;
  if (a == null || c == null) return null;
  return (Number(a) || 0) + (Number(c) || 0);
}

function legsOfRow(row: ExposureRowWithCover, unifierKey: string | undefined, ctx: TopicLegCtx): HedgeExposureRow[] {
  const sym   = row.symbol;
  const cs    = Number(row.contract_size) || ctx.lotSizeOf(sym);
  const price = row.priced !== false && row.price != null ? Number(row.price) : null;
  const rate  = row.quote_usd_rate != null ? Number(row.quote_usd_rate) : null;
  // Coverage is marked at the LP side's price, which can differ from the
  // platform symbol's own (a synthetic member's quote vs the LP's).
  const covPx = row.a_book_mark_price ?? row.c_book_mark_price ?? price;
  // Break-even of a leg from its lots and floating P&L: the price at which
  // the P&L is 0. Unknown without a price, a rate or a position.
  const breakEven = (px: number | null, lots: number, pnl: number | null): number => {
    if (px == null || !rate || !lots || pnl == null) return 0;
    return r5(px - pnl / (lots * cs * rate));
  };
  const leg = (
    id: string, lots: number, pnl: number | null, extra: Partial<HedgeExposureRow> & Pick<HedgeExposureRow, 'symbol' | 'lp' | 'lpAccount' | 'isBBook' | 'source'>,
  ): HedgeExposureRow => {
    const l = r2(lots);
    const px = extra.isBBook ? price : covPx;
    return {
      id, groupSymbol: sym, unifierKey,
      clientNetVol: -l, hedgeNetVol: extra.isBBook ? 0 : l, brokerNetVol: l,
      clientNetNotional: Math.round(-l * cs), hedgeNetNotional: extra.isBBook ? 0 : Math.round(l * cs),
      brokerNetNotional: Math.round(l * cs),
      avgPrice: px != null ? r5(Number(px)) : 0,
      brokerFloatingPL: pnl != null ? r2(pnl) : 0,
      unpriced: pnl == null && l !== 0 ? -1 : 0,
      unhedgedLots: Math.abs(l),
      breakEvenPrice: breakEven(px != null ? Number(px) : null, l, pnl),
      probableIdp30: 'Neutral', bevh: r2(Math.abs(l)), riskLevel: 'Low',
      marketMovePercent: 0, plImpact: 0,
      ...extra,
    };
  };

  const out: HedgeExposureRow[] = [];
  const bCt  = Number(row.b_book_lots_ctrader) || 0;
  const bMt5 = (Number(row.b_book_lots) || 0) - bCt;
  const uCt  = row.unrealized_pnl_ctrader;
  const uAll = row.unrealized_pnl;
  if (bMt5 !== 0) {
    const pnl = uAll == null ? null : Number(uAll) - (Number(uCt) || 0);
    out.push(leg(`ne|${sym}|B|MT5`, bMt5, pnl,
      { symbol: sym, lp: ctx.bLabel, lpAccount: 'Internal', isBBook: true, source: 'MT5' }));
  }
  if (bCt !== 0) {
    out.push(leg(`ne|${sym}|B|CTRADER`, bCt, uCt == null ? null : Number(uCt), {
      symbol: sym, lp: `B-Book-cTrader-${ctx.ctMasterName ?? 'MASTER'}`, lpAccount: 'Books A + B',
      isBBook: true, source: 'CTRADER', stale: !ctx.ctLive,
    }));
  }
  for (const e of row.coverage_by_lp ?? []) {
    const lots = (Number(e.a_lots) || 0) + (Number(e.c_lots) || 0);
    if (!lots && !Number(e.a_lots) && !Number(e.c_lots)) continue;
    if (e.source === 'CTRADER') {
      out.push(leg(`ne|${sym}|C|CTRADER|${e.lp_id}`, lots, coverPnl(e), {
        symbol: sym, lp: 'Coverage-cTrader A-Book', lpAccount: 'cTrader LP',
        isBBook: false, source: 'CTRADER', stale: !ctx.ctLive,
      }));
    } else {
      out.push(leg(`ne|${sym}|C|LP|${e.lp_id}`, lots, coverPnl(e), {
        symbol: ctx.lpSymbolOf(e.lp_id, sym) ?? sym, lp: `Coverage-${ctx.lpName(e.lp_id)}`,
        lpAccount: ctx.lpAccountOf(e.lp_id), isBBook: false, source: 'LP', lpId: e.lp_id,
      }));
    }
  }
  return out;
}

/**
 * Grid legs from one exposure snapshot. Order is the display order (no sort
 * column is active): the top level, unifiers and stand-alone symbols together,
 * by |net_exposure_lots|; members inside their unifier the same way; within a
 * symbol, B-Book legs first, then Coverage.
 */
function legsFromExposure(data: PortfolioExposureSymbolsData | null, ctx: TopicLegCtx): HedgeExposureRow[] {
  if (!data || !Array.isArray(data.by_symbol)) return [];
  const byNet = (a: { net_exposure_lots: number }, b: { net_exposure_lots: number }) =>
    Math.abs(Number(b.net_exposure_lots) || 0) - Math.abs(Number(a.net_exposure_lots) || 0);
  // A by_unifier entry is keyed by `name` (it carries no `symbol`).
  const unifiers = (data.by_unifier ?? []).filter((u) => u && u.name);
  const names = new Set(unifiers.map((u) => u.name));
  const members = new Map<string, ExposureRowWithCover[]>();
  const top: { net_exposure_lots: number; unifier?: string; row?: ExposureRowWithCover }[] = [];
  for (const r of data.by_symbol as ExposureRowWithCover[]) {
    if (!r || !r.symbol) continue;
    if (r.unifier && names.has(r.unifier)) {
      const m = members.get(r.unifier) ?? [];
      m.push(r);
      members.set(r.unifier, m);
    } else {
      top.push({ net_exposure_lots: r.net_exposure_lots, row: r });
    }
  }
  for (const u of unifiers) top.push({ net_exposure_lots: u.net_exposure_lots, unifier: u.name });
  top.sort(byNet);
  const out: HedgeExposureRow[] = [];
  for (const t of top) {
    if (t.row) { out.push(...legsOfRow(t.row, undefined, ctx)); continue; }
    for (const m of (members.get(t.unifier!) ?? []).sort(byNet)) out.push(...legsOfRow(m, t.unifier, ctx));
  }
  return out;
}

interface CTraderDayStats {
  trade_date:  string;
  /** Closed client positions, broker view (all-in). */
  b_realized:  number;
  /** Closed book A positions, the LP position (+profit). */
  a_realized:  number;
  closed_count: number;
}

// A click on a Risk note button opens the note only (no expand, no DOM).
const onRiskNoteButton = (ev: unknown): boolean =>
  !!((ev as { target?: { closest?: (s: string) => unknown } } | null)?.target?.closest?.('[data-risk-note]'));

// ======================
// COMPONENT
// ======================
export function NetExposurePage() {
  const { hasPermission } = useAuth();
  // ── Exposure grid ─────────────────────────────────────────────
  const exposureGridRef = useRef<AgGridReact<HedgeExposureRow>>(null);
  const expandedGroupsRef = useRef<Set<string>>(new Set());
  // True when the user's most recent click was a B-Book row. B-Book positions
  // don't route through the LP, so the DOM Trader panel is muted to make the
  // "can't trade this here" state obvious and to prevent mistaken order entry.
  const [bBookSelected, setBBookSelected] = useState<boolean>(false);

  // ── Display ───────────────────────────────────────────────────
  const [volumeDisplayMode, setVolumeDisplayMode] = useState<'Lots' | 'Notional'>('Notional');

  // ── B-Book data ───────────────────────────────────────────────
  const [bBookPositions, setBBookPositions] = useState<MT5PositionWithNode[]>([]);
  const [bBookNodes,     setBBookNodes]     = useState<MT5NodeAPI[]>([]);
  // filterServer is the master node's node_name. Set once the node list arrives
  // (see effect further down). Only the master is displayed in the dropdown per
  // product decision — non-master MT5 servers are not relevant to this page.
  const [filterServer,   setFilterServer]   = useState<string>('');

  // ── Platforms ─────────────────────────────────────────────────
  // All (default) / MT5 / cTrader. A platform whose MASTER is not connected
  // cannot be chosen (B-Book page rule). platformRef feeds the grid callbacks
  // (aggregates, value getters, row style) without rebuilding the columns.
  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('All');
  const platformRef = useRef<PlatformFilter>(platformFilter);
  platformRef.current = platformFilter;
  const masters = usePlatformMasters();
  // cTrader MASTER positions, both books, from the MT5 socket below.
  const ct = useCTraderMasterPositions(masters.ctMaster);
  const ctHandleRef = useRef<(m: CTraderPositionMessage) => void>(ct.handle);
  ctHandleRef.current = ct.handle;
  const ctMasterIdRef = useRef<number | null>(null);
  ctMasterIdRef.current = masters.ctMaster?.id ?? null;
  // Last known row of each MASTER position, so a DELETE can be realised.
  const ctLastRef = useRef<Map<number, CTraderPositionRow>>(new Map());
  useEffect(() => {
    const m = new Map<number, CTraderPositionRow>();
    for (const r of ct.rows) m.set(r.position_id, r);
    ctLastRef.current = m;
  }, [ct.rows]);
  // cTrader realised today, accumulated client-side like the MT5 B-Book's.
  const [ctDayStats, setCtDayStats] = useState<CTraderDayStats | null>(() => {
    try {
      const raw = localStorage.getItem('nexrisk_ctrader_day_stats');
      if (raw) return JSON.parse(raw) as CTraderDayStats;
    } catch {}
    return null;
  });
  const saveCtDayStats = useCallback((stats: CTraderDayStats | null) => {
    try {
      if (stats) localStorage.setItem('nexrisk_ctrader_day_stats', JSON.stringify(stats));
      else       localStorage.removeItem('nexrisk_ctrader_day_stats');
    } catch {}
  }, []);

  // ── A-Book data — live FIX/LP positions ───────────────────────
  // Every connected Taiga LP's open positions, each tagged with its LP (_lp).
  const [aBookPositions, setABookPositions] = useState<FIXPosition[]>([]);
  const [instrByLp, setInstrByLp] = useState<Record<string, Record<string, FIXInstrument>>>({});

  // ── Header-bar daily aggregates (persist across full-day close-outs) ──────
  // Coverage side: backend-sourced. Same endpoint CBookPage uses — polled on
  // mount, on LP change, and refreshed 500 ms after every POSITION_CLOSED
  // (debounced). realized_pnl, commission, swap_net all roll over at the
  // backend's trade_date boundary.
  const [coverageDailyStats, setCoverageDailyStats] = useState<DailyStats | null>(null);

  // B-Book side: no backend endpoint yet, so accumulate client-side.
  //   1. lastKnownPnlRef captures the current all-in P/L (profit + swap + commission)
  //      for every open B-Book position, updated on every SNAPSHOT / POSITION_CHANGE.
  //   2. On POSITION_DELETE we attribute the cached value as realised P/L,
  //      push it into bBookDayStats, and persist to localStorage.
  //   3. When coverageDailyStats.trade_date advances (backend's rollover signal),
  //      we reset the B-Book accumulator to match — keeps both sides on the same day.
  // This is best-effort until the backend exposes MT5 closed-position aggregates;
  // accuracy may drift by 1 tick's worth of P/L vs actual close price.
  const lastKnownBBookPnlRef = useRef<Map<number, number>>(new Map());

  // Coverage-side per-position P/L cache — mirrors lastKnownBBookPnlRef.
  // TE FIX POSITION_CLOSED events don't carry realized_pnl (confirmed by the
  // FIX_Bridge_API_Documentation POSITION_REPORT schema), so we compute the
  // figure client-side the same way we compute Broker P/L everywhere else:
  //   (close - open) × sign × lots × lotSize
  // using live MD tick prices. Updated on every POSITION_REPORT / price tick
  // so the cache is fresh at the moment of close. On POSITION_CLOSED we pull
  // the cached value and accumulate it into coverageDailyStats.realized_pnl.
  const lastKnownCoveragePnlRef = useRef<Map<string, number>>(new Map());
  const [bBookDayStats, setBBookDayStats] = useState<BBookDayStats | null>(() => {
    try {
      const raw = localStorage.getItem('nexrisk_bbook_day_stats');
      if (raw) return JSON.parse(raw) as BBookDayStats;
    } catch {}
    return null;
  });
  const saveBBookDayStats = useCallback((stats: BBookDayStats | null) => {
    try {
      if (stats) localStorage.setItem('nexrisk_bbook_day_stats', JSON.stringify(stats));
      else       localStorage.removeItem('nexrisk_bbook_day_stats');
    } catch {}
  }, []);

  // ── LP list ───────────────────────────────────────────────────
  const [allLps, setAllLps] = useState<FIXLpEntry[]>(SEED_LPS);
  const isListedLp = useListedLps();
  // Coverage comes from every CONNECTED LP, not only the DOM Trader's.
  const liveLpIds = useMemo(
    () => allLps.filter((l) => l.state === 'CONNECTED').map((l) => l.lp_id).sort(),
    [allLps],
  );
  const liveLpKey = liveLpIds.join(',');
  const liveLpsRef = useRef<Set<string>>(new Set());
  // Netted LP positions seen from REST, by "lp|position_id". A live POSITION_REPORT
  // may come without the netted block and open_price 0; for a position known to be
  // netted that is not a close, so the last netted block is carried over.
  const nettedRef  = useRef<Map<string, NonNullable<FIXPosition['netted']>>>(new Map());
  liveLpsRef.current = new Set(liveLpIds);
  const lpNameOf = useCallback(
    (id: string) => allLps.find((l) => l.lp_id === id)?.lp_name ?? id,
    [allLps],
  );

  // ── LP symbol-mapping table ───────────────────────────────────
  // Loaded once on mount via /api/v1/symbol-mappings. Filtered to
  // enabled+approved rows so half-configured mappings can't redirect
  // grouping. Empty array on fetch failure → page falls back to grouping
  // by raw lp-side symbol (current pre-mapping behaviour preserved).
  const [lpMappings, setLpMappings] = useState<LPMapping[]>([]);

  // ── Exposure snapshot: every symbol figure on the grid ───────
  // portfolio.exposure.symbols over WebSocket; REST seeds the page on mount
  // and stands in only until the first frame (quiet market, weekend).
  const [exposure, setExposure] = useState<PortfolioExposureSymbolsData | null>(null);
  const exposureLiveRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    getPortfolioExposureSymbols()
      .then((d) => { if (!cancelled && !exposureLiveRef.current && d && Array.isArray(d.by_symbol)) setExposure(d); })
      .catch(() => { /* the WS frame fills the grid */ });
    const close = connectPortfolioExposureWebSocket((ev) => {
      if (ev.type !== 'SNAPSHOT' || !ev.data || !Array.isArray(ev.data.by_symbol)) return;
      exposureLiveRef.current = true;
      setExposure(ev.data);
    });
    return () => { cancelled = true; close(); };
  }, []);

  // ── DOM / FIX ─────────────────────────────────────────────────
  const [domLpId,       setDomLpId]       = useState<string>('traderevolution');
  const [domSymbol,     setDomSymbol]     = useState<string>('');
  // DOM Trader panel defaults to collapsed. Opens on Coverage row click (so
  // the user's intent — "trade this symbol" — explicitly summons the panel)
  // or via the chevron on the collapsed-state rail. Kept as state so the
  // entire order form (qty, limit, log) stays mounted across open/close
  // transitions; we're hiding the panel, not tearing it down.
  const [domDrawerOpen, setDomDrawerOpen] = useState<boolean>(false);

  // ── Risk note (left drawer). Each request is a fresh note. ──
  const [riskNoteReq, setRiskNoteReq] = useState<RiskNoteRequest | null>(null);
  const openRiskNote = useCallback((key: string, isUnifier: boolean) => {
    setRiskNoteReq({ key, isUnifier, nonce: Date.now() });
  }, []);
  // Read by the grid's button renderer (column defs stay stable).
  const openRiskNoteRef = useRef(openRiskNote);
  useEffect(() => { openRiskNoteRef.current = openRiskNote; }, [openRiskNote]);
  const [symbolSearch,  setSymbolSearch]  = useState<string>('');
  const [showPicker,    setShowPicker]    = useState(false);
  const [instruments,   setInstruments]   = useState<FIXInstrument[]>([]);
  const [instrLoading,  setInstrLoading]  = useState(false);
  const [capabilities,  setCapabilities]  = useState<FIXCapabilities | null>(null);
  const [lpStatus,      setLpStatus]      = useState<FIXLpStatus | null>(null);
  const [liveBook,      setLiveBook]      = useState<BookData | null>(null);
  const [bookStatus,    setBookStatus]    = useState<string>('—');
  const [domOrderType,  setDomOrderType]  = useState<string>('MARKET');
  const [domTif,        setDomTif]        = useState<string>('GTC');
  const [domQtyLots,    setDomQtyLots]    = useState<string>('');
  const [limitPrice,    setLimitPrice]    = useState<string>('');
  const [submitting,    setSubmitting]    = useState(false);
  const [execLog,       setExecLog]       = useState<ExecEntry[]>([]);
  // Unifier "Point to": targets for the ticket's LP + symbol; '' = None.
  const [domPointTargets, setDomPointTargets] = useState<DomPointTarget[]>([]);
  const [domPointTo,      setDomPointTo]      = useState<string>('');
  const [domToast,        setDomToast]        = useState<string | null>(null);
  const domToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A Coverage leg of a unifier member clicked in the grid: point the ticket
  // to that member once its targets are in (still the user's to change).
  const pendingPointSymRef = useRef<string | null>(null);
  const domPointTargetsRef = useRef<DomPointTarget[]>([]);
  useEffect(() => { domPointTargetsRef.current = domPointTargets; }, [domPointTargets]);
  const showDomToast = useCallback((msg: string) => {
    setDomToast(msg);
    if (domToastTimer.current) clearTimeout(domToastTimer.current);
    domToastTimer.current = setTimeout(() => setDomToast(null), 6000);
  }, []);
  useEffect(() => () => { if (domToastTimer.current) clearTimeout(domToastTimer.current); }, []);

  // ── DOM refs ──────────────────────────────────────────────────
  const wsRef           = useRef<WebSocket | null>(null);
  const liveBookRef     = useRef<BookData | null>(null);
  const localBidsRef    = useRef<Map<number, number>>(new Map());
  const localAsksRef    = useRef<Map<number, number>>(new Map());
  const subscribedRef   = useRef<string>('');
  const wsSymbolRef     = useRef<string>('');
  const wsLpIdRef       = useRef<string>('');
  const bookRenderTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced refresh of Coverage daily stats, called by the WS handler after
  // POSITION_CLOSED. Stored as a ref so the handler (which captures refs, not
  // state/callbacks) always calls the latest version. Populated further down.
  const fetchCoverageDailyStatsRef = useRef<() => void>(() => {});
  const coverageStatsRefreshTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Live price feed for exposure rows (WS-driven) ─────────────
  // symbol+':bid' / symbol+':ask' → latest MD tick. Fed by the FIX WS handler
  // for ANY symbol a position exists on (not just the DOM symbol). Drives
  // Mkt Px and Broker P/L in the exposure grid.
  const currentPricesRef     = useRef<Map<string, number>>(new Map());
  // Symbols we've already posted /md/subscribe for (avoid duplicate subscribes).
  const posSubscribedRef     = useRef<Set<string>>(new Set());
  // Bumps on every MD tick so useMemo-based aggregation re-runs with fresh prices.
  // State, not ref, because useMemo needs a dep that changes. Batched via RAF below.
  const [priceTickCounter, setPriceTickCounter] = useState(0);
  const pendingTickRef       = useRef<boolean>(false);


  // ==========================================================================
  // EFFECTS
  // ==========================================================================

  // ── B-Book: one-shot REST bootstrap + MT5 WebSocket for updates ──────────
  // Pure WS: after the initial snapshot fetch, all B-Book position mutations
  // flow through /ws/v1/mt5/events. The BFF's mt5-ws module emits an initial
  // SNAPSHOT frame (topic='mt5.position', type='SNAPSHOT') followed by
  // incremental events from the C++ MT5 service. Unknown `type`s are ignored
  // with a log line so the incremental event names can be confirmed.
  useEffect(() => {
    let cancelled = false;
    // Bootstrap via REST so the grid is populated before the first WS frame.
    (async () => {
      try {
        const { positions, nodes } = await mt5Api.getAllBBookPositions();
        if (cancelled) return;
        setBBookPositions(positions);
        setBBookNodes(nodes);
      } catch { /* WS will populate */ }
    })();

    // MT5 WebSocket
    const ws = new WebSocket(`${WS_BASE}/ws/v1/mt5/events`);
    ws.onopen    = () => {
      if (cancelled) return;
      console.log('[NetExposure MT5 WS] connected');
      // The cTrader positions reach this socket only once it subscribes.
      ws.send(JSON.stringify({ type: 'subscribe', topics: ['mt5.position', CTRADER_POSITION_TOPIC] }));
    };
    ws.onerror   = (e) => console.warn('[NetExposure MT5 WS] error', e);
    ws.onclose   = () => { if (!cancelled) console.log('[NetExposure MT5 WS] closed'); };
    ws.onmessage = (evt) => {
      if (cancelled) return;
      try {
        const msg = JSON.parse(evt.data);
        // cTrader frames use the same event names on this socket.
        if (isCTraderFrame(msg)) {
          if (msg.topic !== CTRADER_POSITION_TOPIC) return;
          const cm = msg as CTraderPositionMessage;
          // A closed MASTER position: its last known money becomes realised.
          if (cm.type === 'POSITION_DELETE' && cm.data && cm.data.node_id === ctMasterIdRef.current) {
            const last = ctLastRef.current.get(cm.data.position_id) ?? cm.data;
            const { clientPnl, charges } = ctMoney(last);
            setCtDayStats((cur) => {
              const today = new Date().toISOString().slice(0, 10);
              const base  = (cur && cur.trade_date === today) ? cur : { trade_date: today, b_realized: 0, a_realized: 0, closed_count: 0 };
              const next  = {
                ...base,
                b_realized:   base.b_realized - clientPnl + charges,
                a_realized:   base.a_realized + (last.book === 'A' ? clientPnl : 0),
                closed_count: base.closed_count + 1,
              };
              saveCtDayStats(next);
              return next;
            });
          }
          ctHandleRef.current(cm);
          return;
        }

        // All-in P/L for a single MT5 position (matches BBookPage.stats.netPnL).
        const allInPnl = (p: MT5PositionWithNode): number =>
          (p.profit ?? 0) + (p.swap ?? 0) + (p.commission ?? 0);

        // SNAPSHOT — full replacement. Re-seed the last-known-P/L map from the
        // snapshot; any position_id we were tracking that's absent from the
        // snapshot must have closed while the WS was down — attribute its last
        // cached P/L to realised before dropping it.
        if (msg.type === 'SNAPSHOT' && Array.isArray(msg.data)) {
          const incoming = msg.data as MT5PositionWithNode[];
          const prevMap = lastKnownBBookPnlRef.current;
          const newMap  = new Map<number, number>();
          for (const p of incoming) newMap.set(p.position_id, allInPnl(p));
          // Positions that disappeared between snapshots → realise them.
          let gapRealised = 0; let gapCount = 0;
          for (const [pid, pnl] of prevMap) {
            if (!newMap.has(pid)) { gapRealised += pnl; gapCount += 1; }
          }
          if (gapCount > 0) {
            setBBookDayStats((cur) => {
              const today = new Date().toISOString().slice(0, 10);
              const base  = (cur && cur.trade_date === today) ? cur : { trade_date: today, realized_pnl: 0, closed_count: 0 };
              const next  = { ...base, realized_pnl: base.realized_pnl + gapRealised, closed_count: base.closed_count + gapCount };
              saveBBookDayStats(next);
              return next;
            });
          }
          lastKnownBBookPnlRef.current = newMap;
          setBBookPositions(incoming);
          return;
        }

        // Upsert a single MT5 position into the B-Book grid. Merge — DO NOT
        // replace. The C++ backend's per-position event is a delta: it carries
        // only the fields that change tick-to-tick (price_current, profit, swap,
        // commission) and omits the static lifetime fields (action, volume_lots,
        // price_open, time_create etc.). A naïve `next[idx] = p` would strip
        // those from the row, turning Net Vol into NaN and Mkt Px / Break-Even
        // Px into 0 the first time a tick arrives after the REST/SNAPSHOT
        // bootstrap. Spreading the existing row first preserves the static
        // fields; the delta then overwrites only what it actually contains.
        const upsertBBookPosition = (p: MT5PositionWithNode | undefined): void => {
          if (!p || !p.nodeName) return;
          lastKnownBBookPnlRef.current.set(p.position_id, allInPnl(p));
          const keyOf = (pp: MT5PositionWithNode) =>
            `${pp.nodeName}:${(pp as any).position_id ?? (pp as any).ticket ?? ''}`;
          const k = keyOf(p);
          setBBookPositions((prev) => {
            const idx = prev.findIndex((q) => keyOf(q) === k);
            if (idx >= 0) { const next = [...prev]; next[idx] = { ...next[idx], ...p }; return next; }
            return [...prev, p];
          });
        };

        // Single position add/change (real MT5 state change). Event names per
        // api.ts BBookWsEvent union.
        if (msg.type === 'POSITION_ADD' || msg.type === 'POSITION_CHANGE') {
          upsertBBookPosition(msg.data as MT5PositionWithNode | undefined);
          return;
        }

        // Batch per-tick P/L stream (new). The discriminator may sit on the
        // envelope (msg.type) or inside the payload (msg.data.type), and the
        // positions array likewise — the C++ wire shape for this frame is
        // unverified, so accept either placement. Each element is the same
        // position object as the single path, so reuse the identical upsert.
        if (msg.type === 'POSITION_BATCH' || msg.data?.type === 'POSITION_BATCH') {
          const batch = msg.data?.positions ?? msg.positions;
          if (Array.isArray(batch)) {
            for (const p of batch) upsertBBookPosition(p as MT5PositionWithNode);
          }
          return;
        }

        if (msg.type === 'POSITION_DELETE') {
          const pid = msg.data?.position_id;
          if (pid == null) return;
          // Attribute the last cached P/L as realised. Drop the cache entry.
          const cachedPnl = lastKnownBBookPnlRef.current.get(pid) ?? 0;
          lastKnownBBookPnlRef.current.delete(pid);
          setBBookDayStats((cur) => {
            const today = new Date().toISOString().slice(0, 10);
            const base  = (cur && cur.trade_date === today) ? cur : { trade_date: today, realized_pnl: 0, closed_count: 0 };
            const next  = { ...base, realized_pnl: base.realized_pnl + cachedPnl, closed_count: base.closed_count + 1 };
            saveBBookDayStats(next);
            return next;
          });
          setBBookPositions((prev) => prev.filter((q) =>
            ((q as any).position_id ?? (q as any).ticket) !== pid
          ));
          return;
        }
        // Subscription ack / pong — ignore quietly.
        if (msg.type === 'subscribed' || msg.type === 'pong') return;
        if (msg.type) console.debug('[NetExposure MT5 WS] unhandled type:', msg.type);
      } catch { /* malformed */ }
    };

    return () => { cancelled = true; ws.close(); };
  }, []);

  // ── Coverage Book: one-shot REST bootstrap — WebSocket drives updates afterwards.
  // This page is pure WebSocket: after the initial snapshot, POSITION_REPORT,
  // POSITION_CLOSED, and POSITION_UPDATED events on the shared FIX WS (opened in
  // the DOM effect below) mutate aBookPositions in place. No polling timer.
  // Uses the v3 flat-path endpoint — the only one that returns the full
  // operational set including hedging-strategy LP positions.
  // Closed-position tombstones (populated by CBookPage on close) are honoured so a
  // just-closed position doesn't ghost back in before the WS tombstone arrives.
  useEffect(() => {
    if (!liveLpIds.length) { setABookPositions([]); return; }
    let cancelled = false;
    const readClosedIds = (): Set<string> => {
      try {
        const raw = localStorage.getItem('nexrisk_closed_positions');
        if (raw) return new Set<string>(JSON.parse(raw) as string[]);
      } catch {}
      return new Set<string>();
    };
    (async () => {
      const closedIds = readClosedIds();
      const results = await Promise.allSettled(liveLpIds.map(async (lp) => {
        const [posR, insR] = await Promise.allSettled([
          bff<{ success: boolean; data: { positions: FIXPosition[] } }>(`/api/v1/fix/positions/${lp}`),
          bff<{ success: boolean; data: { instruments: FIXInstrument[] } }>(`/api/v1/fix/lp/${lp}/instruments`),
        ]);
        const all = posR.status === 'fulfilled' && posR.value.success ? posR.value.data.positions ?? [] : [];
        for (const p of all) if (p.position_id && p.netted) nettedRef.current.set(`${lp}|${p.position_id}`, p.netted);
        const kept = all.filter((p) => isOpenLpPosition(p) && (!closedIds.has(p.position_id) || nettedHeld(p)));
        if (all.length !== kept.length) {
          // eslint-disable-next-line no-console
          console.warn(`[NetExposure] ${lp}: ${all.length - kept.length} of ${all.length} positions not shown (empty position_id, open_price 0 or closed this session)`,
            all.filter((p) => !kept.includes(p)).slice(0, 5));
        }
        const instr: Record<string, FIXInstrument> = {};
        if (insR.status === 'fulfilled' && insR.value.success) {
          for (const i of insR.value.data.instruments ?? []) instr[i.symbol] = i;
        }
        return { lp, positions: kept.map((p) => ({ ...p, _lp: lp })), instr };
      }));
      if (cancelled) return;
      const positions: FIXPosition[] = [];
      const byLp: Record<string, Record<string, FIXInstrument>> = {};
      for (const r of results) {
        if (r.status !== 'fulfilled') continue;
        positions.push(...r.value.positions);
        byLp[r.value.lp] = r.value.instr;
      }
      setInstrByLp(byLp);
      setABookPositions(positions);
    })();
    return () => { cancelled = true; };
  }, [liveLpKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── LP list: seed from /fix/status ────────────────────────────
  useEffect(() => {
    let cancelled = false;
    // /api/v1/fix/status returns UNWRAPPED JSON (verified live 2026-08-08):
    //   { bridge_id, state, lps: [ { lp_id, provider_type, state } ] }
    // No { success, data } envelope; lps is an ARRAY. lp_name is absent from
    // status entries, so display names are enriched best-effort from the LP
    // Admin registry (GET /api/v1/fix/admin/lp).
    const namesPromise: Promise<Record<string, string>> = bff<any>('/api/v1/fix/admin/lp')
      .then((r) => {
        const list = r?.data?.lps ?? r?.lps ?? [];
        const map: Record<string, string> = {};
        for (const e of (Array.isArray(list) ? list : Object.values(list)) as any[]) {
          if (e?.lp_id && e?.lp_name) map[e.lp_id] = e.lp_name;
        }
        return map;
      })
      .catch(() => ({} as Record<string, string>));
    Promise.all([bff<any>('/api/v1/fix/status'), namesPromise])
      .then(([r, names]) => {
        if (cancelled) return;
        const rawLps = r?.data?.lps ?? r?.lps ?? null;
        if (!rawLps) return; // unexpected shape — seed data shown
        const entries: any[] = Array.isArray(rawLps)
          ? rawLps
          : Object.entries(rawLps).map(([id, info]: [string, any]) => ({ lp_id: id, ...info }));
        const live: FIXLpEntry[] = entries
          .filter((e: any) => e?.lp_id)
          .map((e: any) => ({
            lp_id:         e.lp_id,
            lp_name:       e.lp_name ?? names[e.lp_id] ?? SEED_LPS.find((s) => s.lp_id === e.lp_id)?.lp_name ?? e.lp_id,
            state:         e.state   ?? 'UNKNOWN',
            provider_type: e.provider_type ?? '',
          }));
        for (const seed of SEED_LPS) {
          if (!live.find((l) => l.lp_id === seed.lp_id)) live.push({ ...seed, state: 'DISCONNECTED' });
        }
        setAllLps(live);
        // Auto-select first connected LP
        const first = live.find((l) => l.state === 'CONNECTED');
        // The DOM Trader starts on a connected LP; a seed default that is not
        // connected gives way to the first one that is.
        if (first) setDomLpId((prev) =>
          prev && live.some((l) => l.lp_id === prev && l.state === 'CONNECTED') ? prev : first.lp_id);
      })
      .catch(() => { /* seed data shown */ })
      .finally(() => { if (!cancelled) {} });
    return () => { cancelled = true; };
  }, []);

  // ── LP symbol mappings: one-shot fetch ────────────────────────
  // Loaded once at mount. Deliberately NOT keyed on domLpId — mappings for
  // every LP are needed at once so a Coverage-row symbol on LP A can be
  // resolved even when DOM/order-entry is currently focused on LP B.
  // No WS topic for mapping changes today, so a page refresh is required
  // after editing on the Symbol Mapping page (acceptable: that page already
  // operates on a refresh-to-see-changes model).
  useEffect(() => {
    let cancelled = false;
    bff<{ mappings: LPMapping[] }>('/api/v1/symbol-mappings')
      .then((r) => {
        if (cancelled) return;
        setLpMappings((r.mappings ?? []).filter((m) => m.enabled));
      })
      .catch(() => { /* table empty → page falls back to raw lp_symbol grouping */ });
    return () => { cancelled = true; };
  }, []);

  // ── DOM "Point to" targets ────────────────────────────────────
  // Re-read whenever the ticket's LP or symbol changes. No targets → no field.
  useEffect(() => {
    setDomPointTargets([]); setDomPointTo('');
    if (!domLpId || !domSymbol) return;
    let cancelled = false;
    const qs = new URLSearchParams({ lp_id: domLpId, lp_symbol: domSymbol });
    bff<{ targets?: DomPointTarget[]; data?: { targets?: DomPointTarget[] } }>(`/api/v1/mappings/unified/dom-targets?${qs}`)
      .then((r) => {
        if (cancelled) return;
        const list = (r.targets ?? r.data?.targets ?? []).filter((t) => t && t.symbol && t.platform);
        setDomPointTargets(list);
        const fromRow = pendingPointSymRef.current
          ? list.find((t) => t.symbol === pendingPointSymRef.current) : undefined;
        pendingPointSymRef.current = null;
        const last = readPointStore()[`${domLpId}|${domSymbol}`];
        if (fromRow) setDomPointTo(pointKey(fromRow));
        else if (last && list.some((t) => pointKey(t) === last)) setDomPointTo(last);
      })
      .catch(() => { /* no targets: the order goes without a pointer, as before */ });
    return () => { cancelled = true; };
  }, [domLpId, domSymbol]);

  // ── Instruments + capabilities for DOM ───────────────────────
  useEffect(() => {
    if (!domLpId) { setInstruments([]); setCapabilities(null); return; }
    let cancelled = false;
    setInstrLoading(true);
    Promise.allSettled([
      bff<{ success: boolean; data: { instruments: FIXInstrument[] } }>(`/api/v1/fix/lp/${domLpId}/instruments`),
      bff<{ success: boolean; data: FIXCapabilities }>(`/api/v1/fix/lp/${domLpId}/capabilities`),
    ]).then(([instrR, capsR]) => {
      if (cancelled) return;
      if (instrR.status === 'fulfilled' && instrR.value.success) {
        setInstruments(instrR.value.data.instruments);
      }
      if (capsR.status === 'fulfilled' && capsR.value.success) {
        setCapabilities(capsR.value.data);
        setDomTif(capsR.value.data.time_in_force[0] || 'GTC');
      }
    }).finally(() => { if (!cancelled) setInstrLoading(false); });
    return () => { cancelled = true; };
  }, [domLpId]);

  // ── FIX WebSocket — opens per LP, stays up across DOM symbol changes ─────
  // This is the sole source of position + market-data updates for this page.
  // The DOM-symbol MD subscribe and per-position-symbol MD subscribe effects
  // below tell the backend which symbols to stream; all resulting frames land
  // here. Position events mutate aBookPositions in place (no polling).
  useEffect(() => {
    if (!domLpId) {
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      wsLpIdRef.current = '';
      setBookStatus('—');
      return;
    }
    let cancelled = false;
    wsLpIdRef.current = domLpId;

    const readClosedIds = (): Set<string> => {
      try {
        const raw = localStorage.getItem('nexrisk_closed_positions');
        if (raw) return new Set<string>(JSON.parse(raw) as string[]);
      } catch {}
      return new Set<string>();
    };

    // Coalesce price-tick state bumps to one per animation frame — MD can fire
    // many times per second per symbol and we don't want to re-render that often.
    const scheduleTick = () => {
      if (pendingTickRef.current) return;
      pendingTickRef.current = true;
      requestAnimationFrame(() => {
        pendingTickRef.current = false;
        setPriceTickCounter((n) => n + 1);
      });
    };

    const ws = new WebSocket(`${WS_BASE}${FIX_WS_PATH}`);
    wsRef.current = ws;

    ws.onopen = () => {
      if (cancelled) { ws.close(); return; }
      setBookStatus((prev) => prev === 'HEALTHY' ? 'HEALTHY' : 'EMPTY');
    };

    ws.onmessage = (evt) => {
      if (cancelled) return;
      try {
        const msg = JSON.parse(evt.data);

        // ── Market data — any symbol, feeds currentPricesRef ─────────────
        if (msg.type === 'MARKET_DATA_SNAPSHOT' || msg.type === 'MARKET_DATA_INCREMENTAL' ||
            msg.type === 'MD_SNAPSHOT' || msg.type === 'MD_INCREMENTAL') {
          const bookRaw = msg.data ?? msg;
          const sym = bookRaw.symbol ?? msg.symbol;
          if (!sym) return;
          const mdLp: string = msg.lp_id ?? wsLpIdRef.current;

          // DOM panel book — only the selected DOM symbol of the DOM LP drives the depth view.
          if (sym === wsSymbolRef.current && mdLp === wsLpIdRef.current) {
            const mutated = applyBookMessage(bookRaw, msg.type, localBidsRef.current, localAsksRef.current);
            if (mutated) {
              const bd = buildBookFromMaps(sym, localBidsRef.current, localAsksRef.current);
              if (bd) {
                liveBookRef.current = bd;
                setBookStatus('HEALTHY');
                if (!bookRenderTimer.current) {
                  bookRenderTimer.current = setTimeout(() => {
                    bookRenderTimer.current = null;
                    if (liveBookRef.current) setLiveBook({ ...liveBookRef.current });
                  }, 100);
                }
              }
            }
          }

          // Exposure grid prices — every symbol with a position feeds here.
          const refBid: number | null = bookRaw.best_bid ?? null;
          const refAsk: number | null = bookRaw.best_ask ?? null;
          if (refBid != null && refAsk != null) {
            const pk = `${mdLp}|${sym}`;
            const prevBid = currentPricesRef.current.get(pk + ':bid');
            const prevAsk = currentPricesRef.current.get(pk + ':ask');
            if (prevBid !== refBid || prevAsk !== refAsk) {
              currentPricesRef.current.set(pk + ':bid', refBid);
              currentPricesRef.current.set(pk + ':ask', refAsk);
              scheduleTick();
            }
          }
          return;
        }

        // ── Position events — mutate Coverage Book state ──────────────────
        if (msg.type === 'POSITION_REPORT') {
          const evtLp: string = msg.lp_id ?? wsLpIdRef.current;
          if (!liveLpsRef.current.has(evtLp)) return;
          const pd = msg.data ?? msg;
          const pid: string = pd.position_id ?? '';
          if (!pid) return;
          const knownNetted = pd.netted ?? nettedRef.current.get(`${evtLp}|${pid}`);
          if (readClosedIds().has(pid) && !nettedHeld({ netted: knownNetted } as FIXPosition)) return;

          const pos: FIXPosition = {
            position_id: pid,
            account:     pd.account     ?? '',
            symbol:      pd.symbol      ?? '',
            open_price:  pd.open_price  ?? 0,
            long_qty:    pd.long_qty    ?? 0,
            short_qty:   pd.short_qty   ?? 0,
            net_qty:     pd.net_qty     ?? 0,
            side:        pd.side        ?? 'FLAT',
            commission:  pd.commission  ?? 0,
            swap:        pd.swap        ?? 0,
            received_ts: msg.timestamp_ms ?? Date.now(),
            unrealized_pnl: pd.unrealized_pnl ?? undefined,
            current_price:  pd.current_price  ?? undefined,
            contract_size:  pd.contract_size  ?? undefined,
            canonical_symbol: pd.canonical_symbol ?? undefined,
            netted:      pd.netted      ?? nettedRef.current.get(`${evtLp}|${pid}`),
            _lp:         evtLp,
          };
          if (pd.netted) nettedRef.current.set(`${evtLp}|${pid}`, pd.netted);
          // open_price === 0 on a TE POSITION_REPORT signals external close.
          // A netted position at open_price 0 is partly untracked, not closed.
          if (pos.open_price === 0 && !pos.netted) {
            setABookPositions((prev) => prev.filter((p) => !(p.position_id === pid && p._lp === evtLp)));
            return;
          }
          if (isOpenLpPosition(pos) && (pos.long_qty + pos.short_qty > 0 || Math.abs(pos.net_qty) > 0 || !!pos.netted)) {
            setABookPositions((prev) => {
              const idx = prev.findIndex((p) => p.position_id === pid && p._lp === evtLp);
              if (idx >= 0) { const next = [...prev]; next[idx] = pos; return next; }
              return [...prev, pos];
            });
          }
          return;
        }

        if (msg.type === 'POSITION_CLOSED') {
          const evtLp: string = msg.lp_id ?? wsLpIdRef.current;
          if (!liveLpsRef.current.has(evtLp)) return;
          const pid = msg.position_id ?? msg.data?.position_id;
          if (!pid) return;

          // Realised P/L for this close. TE FIX POSITION_CLOSED does NOT
          // include realized_pnl in its payload (see FIX_Bridge_API_Documentation
          // POSITION_REPORT schema — the same event shape, just with net_qty=0),
          // so we read the last computed per-position P/L from our cache.
          // Updated on every MD tick by the hedgeExposureData memo above.
          // If the cache is empty (close arrived before any tick), fall back to 0
          // and let the 500ms debounced refetch correct from backend.
          const cachedPnl = lastKnownCoveragePnlRef.current.get(`${evtLp}|${pid}`) ?? 0;
          lastKnownCoveragePnlRef.current.delete(`${evtLp}|${pid}`);

          // Accumulate into coverageDailyStats immediately so the header card
          // reflects the close without waiting for a REST round-trip.
          if (cachedPnl !== 0) {
            setCoverageDailyStats((prev) => prev
              ? {
                  ...prev,
                  realized_pnl:   prev.realized_pnl   + cachedPnl,
                  position_count: Math.max(0, prev.position_count - 1),
                }
              : {
                  trade_date:     new Date().toISOString().slice(0, 10),
                  realized_pnl:   cachedPnl,
                  commission:     0,
                  swap_long:      null,
                  swap_short:     null,
                  swap_net:       0,
                  position_count: 0,
                }
            );
          }

          setABookPositions((prev) => prev.filter((p) => !(p.position_id === pid && p._lp === evtLp)));

          // Debounced refresh so if the backend ever starts reporting realised
          // P/L authoritatively, its value overrides our client-side estimate.
          if (coverageStatsRefreshTimer.current) clearTimeout(coverageStatsRefreshTimer.current);
          coverageStatsRefreshTimer.current = setTimeout(() => fetchCoverageDailyStatsRef.current(), 500);
          return;
        }

        if (msg.type === 'POSITION_UPDATED') {
          const evtLp: string = msg.lp_id ?? wsLpIdRef.current;
          if (!liveLpsRef.current.has(evtLp)) return;
          const upd = msg.data ?? msg;
          const pid = upd.position_id ?? upd.positionId;
          if (!pid) return;
          setABookPositions((prev) => prev.map((p) => p.position_id === pid && p._lp === evtLp ? {
            ...p,
            long_qty:  upd.long_qty  ?? p.long_qty,
            short_qty: upd.short_qty ?? p.short_qty,
            net_qty:   upd.net_qty   ?? p.net_qty,
          } : p));
          return;
        }

        // ── Session ───────────────────────────────────────────────────
        if (msg.type === 'SESSION_LOGON')  setBookStatus((prev) => prev === 'HEALTHY' ? 'HEALTHY' : 'EMPTY');
        if (msg.type === 'SESSION_LOGOUT') setBookStatus('DISCONNECTED');
      } catch { /* malformed frame */ }
    };

    ws.onerror = (e) => console.warn('[NetExposure WS] error', e);
    ws.onclose = () => { if (!cancelled) console.log('[NetExposure WS] closed'); };

    return () => {
      cancelled = true;
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      if (bookRenderTimer.current) { clearTimeout(bookRenderTimer.current); bookRenderTimer.current = null; }
      wsLpIdRef.current = '';
      // Unsubscribe every '<lp>|<symbol>' we subscribed.
      for (const k of posSubscribedRef.current) {
        const [lp, sym] = k.split('|');
        bff('/api/v1/fix/md/unsubscribe', {
          method: 'POST', body: JSON.stringify({ lp_id: lp, symbol: sym }),
        }).catch(() => {});
      }
      posSubscribedRef.current.clear();
      currentPricesRef.current.clear();
    };
  }, [domLpId]);

  // ── DOM-symbol MD subscription (depth 10) — for the book-depth panel ─────
  // Separate from the FIX WS lifecycle above so that picking a new DOM symbol
  // only resubscribes MD — it does NOT tear down the WS or the position stream.
  useEffect(() => {
    if (!domLpId || !domSymbol) {
      setLiveBook(null); liveBookRef.current = null;
      wsSymbolRef.current = '';
      localBidsRef.current.clear(); localAsksRef.current.clear();
      return;
    }

    const key = `${domLpId}:${domSymbol}`;
    if (subscribedRef.current === key) return;
    subscribedRef.current = key;
    wsSymbolRef.current   = domSymbol;
    localBidsRef.current.clear(); localAsksRef.current.clear();
    setLiveBook(null); liveBookRef.current = null;
    setBookStatus('SUBSCRIBING');
    // Ensure we're tracking this symbol for per-position prices too.
    posSubscribedRef.current.add(`${domLpId}|${domSymbol}`);

    let cancelled = false;
    (async () => {
      // Check LP session (non-fatal).
      try {
        const st = await bff<{ success: boolean; data: FIXLpStatus }>(`/api/v1/fix/status/${domLpId}`);
        if (!cancelled && st.success) setLpStatus(st.data);
      } catch { /* non-fatal */ }
      // Subscribe at depth 10 for the DOM book panel.
      try {
        await bff(`/api/v1/fix/md/subscribe`, {
          method: 'POST', body: JSON.stringify({ lp_id: domLpId, symbol: domSymbol, depth: 10 }),
        });
      } catch { /* non-fatal */ }
      if (cancelled) return;
      // TE first snapshot lands within ~400 ms; seed local maps from REST cache after.
      await new Promise((r) => setTimeout(r, 400));
      if (cancelled) return;
      try {
        const bookR = await bff<any>(`/api/v1/fix/md/book/${domLpId}/${domSymbol}`);
        if (!cancelled) {
          primeBookFromRest(bookR, localBidsRef.current, localAsksRef.current);
          const bd = buildBookFromMaps(domSymbol, localBidsRef.current, localAsksRef.current);
          if (bd) { liveBookRef.current = bd; setLiveBook(bd); setBookStatus('HEALTHY'); }
        }
      } catch { /* non-fatal */ }
    })();

    return () => { cancelled = true; };
  }, [domLpId, domSymbol]);

  // ── Per-position MD subscription (depth 1) — live Mkt Px + P/L every row ─
  // Every symbol that has an open position in either book gets a depth-1 MD
  // subscription so the FIX WS handler above feeds currentPricesRef for it.
  // Subscriptions accumulate for the LP session and are torn down when the LP
  // changes (the FIX WS cleanup unsubscribes the full set).
  useEffect(() => {
    // LP positions are priced at their own LP; B-Book symbols at the DOM LP.
    const want: Array<[string, string]> = [];
    for (const p of aBookPositions) {
      if (!p.symbol || !p._lp) continue;
      want.push([p._lp, p.symbol]);
      // The USD conversion symbol of a non-USD-quoted pair, at the same LP.
      const instr = instrByLp[p._lp] ?? {};
      const conv = conversionSymbolOf(p.symbol, instr[p.symbol]?.currency, (c) => !!instr[c]);
      if (conv) want.push([p._lp, conv]);
    }
    if (domLpId) for (const p of bBookPositions) if (p.symbol) want.push([domLpId, p.symbol]);
    for (const [lp, sym] of want) {
      const k = `${lp}|${sym}`;
      if (posSubscribedRef.current.has(k)) continue;
      posSubscribedRef.current.add(k);
      bff('/api/v1/fix/md/subscribe', {
        method: 'POST', body: JSON.stringify({ lp_id: lp, symbol: sym, depth: 1 }),
      }).catch(() => {});
    }
  }, [aBookPositions, bBookPositions, domLpId, instrByLp]);

  // ── Coverage daily stats — poll every 30 s as a safety net, plus WS-driven
  // refresh after POSITION_CLOSED (via fetchCoverageDailyStatsRef above).
  // Summed over every connected LP.
  const fetchCoverageDailyStats = useCallback(() => {
    if (!liveLpIds.length) { setCoverageDailyStats(null); return; }
    Promise.allSettled(liveLpIds.map((lp) => bff<{ success: boolean; data: DailyStats }>(
      `/api/v1/fix/daily-stats?lp_id=${encodeURIComponent(lp)}`
    ))).then((rs) => {
      const got = rs.flatMap((r) => (r.status === 'fulfilled' && r.value.success ? [r.value.data] : []));
      if (!got.length) return;
      setCoverageDailyStats({
        trade_date:     got[0].trade_date,
        realized_pnl:   got.reduce((a, d) => a + (d.realized_pnl ?? 0), 0),
        commission:     got.reduce((a, d) => a + (d.commission ?? 0), 0),
        swap_long:      null,
        swap_short:     null,
        swap_net:       got.reduce((a, d) => a + (d.swap_net ?? 0), 0),
        position_count: got.reduce((a, d) => a + (d.position_count ?? 0), 0),
      });
    });
  }, [liveLpKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCoverageDailyStatsRef.current = fetchCoverageDailyStats; }, [fetchCoverageDailyStats]);

  useEffect(() => {
    if (!liveLpIds.length) { setCoverageDailyStats(null); return; }
    fetchCoverageDailyStats();
    const timer = setInterval(fetchCoverageDailyStats, 30_000);
    return () => clearInterval(timer);
  }, [liveLpKey, fetchCoverageDailyStats]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── B-Book daily stats rollover — when the backend's trade_date advances,
  // reset the client-side accumulator to match. Uses Coverage's trade_date as
  // the authoritative day marker (same pattern as CBookPage strategyDayStats).
  useEffect(() => {
    const td = coverageDailyStats?.trade_date;
    if (!td) return;
    setBBookDayStats((cur) => {
      if (cur && cur.trade_date === td) return cur;
      const next = { trade_date: td, realized_pnl: 0, closed_count: 0 };
      saveBBookDayStats(next);
      return next;
    });
  }, [coverageDailyStats?.trade_date, saveBBookDayStats]);
  useEffect(() => {
    const td = coverageDailyStats?.trade_date;
    if (!td) return;
    setCtDayStats((cur) => {
      if (cur && cur.trade_date === td) return cur;
      const next = { trade_date: td, b_realized: 0, a_realized: 0, closed_count: 0 };
      saveCtDayStats(next);
      return next;
    });
  }, [coverageDailyStats?.trade_date, saveCtDayStats]);

  // ==========================================================================
  // DERIVED
  // ==========================================================================
  const instrMap = useMemo<Record<string, FIXInstrument>>(() => {
    const map: Record<string, FIXInstrument> = {};
    for (const ins of instruments) map[ins.symbol] = ins;
    return map;
  }, [instruments]);

  // Identify the master node — there must be exactly one (see MT5 Servers page).
  // Fall back to the first node only if nothing is flagged as master so the UI
  // still renders during config setup; in normal operation is_master is set.
  const masterNode = useMemo(() => {
    return bBookNodes.find((n) => n.is_master) ?? bBookNodes[0] ?? null;
  }, [bBookNodes]);

  const bBookLpLabel = useMemo(() => {
    // Always shows the master's node_name — non-master nodes aren't represented
    // in this page's exposure grid per product decision.
    return masterNode ? `B-Book-${masterNode.node_name}` : 'B-Book';
  }, [masterNode]);

  // Only the MT5 MASTER's positions are in the book. filterServer holds its
  // node_name so the bBookPositions filter below matches p.nodeName as
  // stamped by mt5-ws.ts. (The MT5 node dropdown gave way to the Platform
  // filter; there is nothing else to choose.)
  // Auto-select the master as soon as we know which one it is — only when
  // filterServer hasn't been set yet, so we don't clobber a manual selection.
  useEffect(() => {
    if (!masterNode) return;
    if (!filterServer) setFilterServer(masterNode.node_name);
  }, [masterNode, filterServer]);

  // mt5_symbol → instrument-class flags. Built from LP mappings: classify each
  // mt5_symbol by its corresponding lp_symbol (which uses standardised names
  // like 'XAUUSD' / 'EURJPY' / 'BTCUSD'). Lets the B-Book aggregator and the
  // header-bar volume rollups apply the right lotSize/pipValue for broker-
  // specific symbols like 'Gold' that don't include 'XAU' in their name.
  // Ambiguous case (one mt5_symbol mapped to lp_symbols of different classes)
  // is resolved first-write-wins; in practice that doesn't happen because a
  // single instrument doesn't change asset class across LPs.
  const mt5SymbolClass = useMemo(() => {
    const m = new Map<string, Mt5SymbolClass>();
    for (const mp of lpMappings) {
      if (m.has(mp.mt5_symbol)) continue;
      const lpSym = mp.lp_symbol;
      m.set(mp.mt5_symbol, {
        isJPY: lpSym.includes('JPY'),
        isXAU: lpSym.includes('XAU'),
        isBTC: lpSym.includes('BTC'),
      });
    }
    return m;
  }, [lpMappings]);


  // Refresh the per-position Coverage P/L cache used by the POSITION_CLOSED
  // handler (header realised P/L). Positions only — no grid figure comes
  // from here any more.
  useEffect(() => {
    const livePrices = currentPricesRef.current;
    // Math: (close - open) × sign × lots × lotSize: (close - open) × sign × lots × lotSize, where `close` is
    // bid for long legs / ask for short legs.
    const cov = lastKnownCoveragePnlRef.current;
    for (const p of aBookPositions) {
      if (!isOpenLpPosition(p)) continue;
      const isLong = p.side === 'LONG' || p.side === 'BUY'
        || (p.long_qty > 0 && p.short_qty === 0);
      const isShort = p.side === 'SHORT' || p.side === 'SELL'
        || (p.short_qty > 0 && p.long_qty === 0);
      const nn = nettedNet(p);
    const sign = nn != null ? (nn >= 0 ? 1 : -1) : isLong ? 1 : isShort ? -1 : (p.net_qty >= 0 ? 1 : -1);
      const ins = (instrByLp[p._lp ?? domLpId] ?? {})[p.symbol];
      const isXAU = p.symbol.includes('XAU');
      const isBTC = p.symbol.includes('BTC');
      const lotSize = isXAU ? 100 : isBTC ? 1 : 100000;
      const minVol  = ins?.min_trade_vol ?? (isXAU ? 10 : isBTC ? 1 : 100000);
      const qtyContracts = nn != null ? Math.abs(nn) : Math.abs(p.net_qty) || Math.max(p.long_qty, p.short_qty);
      if (qtyContracts === 0) continue;
      // An LP whose quantities are contracts (LMAX) gives each one's size.
      const lots = p.contract_size ? qtyContracts * p.contract_size / lotSize
        : p.netted ? qtyContracts / lotSize : (qtyContracts * minVol) / lotSize;
      const pk  = `${p._lp ?? domLpId}|${p.symbol}`;
      const bid = livePrices.get(pk + ':bid');
      const ask = livePrices.get(pk + ':ask');
      if (bid == null || ask == null) continue; // no tick yet — leave stale entry
      const close = sign > 0 ? bid : ask;
      const priced = lpPricedPart(p, lots, lotSize);
      // Quote currency → USD, like the leg's Broker P/L. No rate: leave the entry as it was.
      const quotePnl = (close - priced.px) * sign * priced.lots * lotSize;
      const lpOf = p._lp ?? domLpId;
      const instrOf = instrByLp[lpOf] ?? {};
      const conv = conversionSymbolOf(p.symbol, instrOf[p.symbol]?.currency, (c) => !!instrOf[c]);
      const cb = conv ? livePrices.get(`${lpOf}|${conv}:bid`) : undefined;
      const ca = conv ? livePrices.get(`${lpOf}|${conv}:ask`) : undefined;
      const usd = toUsd(quotePnl, p.symbol, instrOf[p.symbol]?.currency, (bid + ask) / 2,
        conv, cb != null && ca != null ? (cb + ca) / 2 : null);
      if (usd === null) continue;
      const pnl = usd + (p.swap ?? 0) + (p.commission ?? 0);
      cov.set(`${lpOf}|${p.position_id}`, pnl);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aBookPositions, domLpId, instrByLp, priceTickCounter]);

  // Grid legs, all from the exposure snapshot (see legsFromExposure).
  const hedgeExposureData = useMemo(() => {
    const accountOf = new Map<string, string>();
    for (const p of aBookPositions) if (p._lp && p.account && !accountOf.has(p._lp)) accountOf.set(p._lp, p.account);
    const lpSymOf = new Map<string, string>();
    for (const m of lpMappings) {
      const k = `${m.lp_id}|${m.mt5_symbol}`;
      if (!lpSymOf.has(k)) lpSymOf.set(k, m.lp_symbol);
    }
    return legsFromExposure(exposure, {
      bLabel:       bBookLpLabel,
      ctMasterName: masters.ctMaster?.name ?? null,
      ctLive:       !!masters.ctActive,
      lpName:       lpNameOf,
      lpSymbolOf:   (lp, sym) => lpSymOf.get(`${lp}|${sym}`),
      lpAccountOf:  (lp) => accountOf.get(lp) ?? '',
      lotSizeOf:    (sym) => symLotSize(sym, mt5SymbolClass),
    });
  }, [exposure, aBookPositions, lpMappings, bBookLpLabel, masters.ctMaster, masters.ctActive, lpNameOf, mt5SymbolClass]);

  // What the grid shows under the Platform filter. Taiga's LP leg stays under
  // either platform (marked "All platforms", left out of the sums).
  const gridRows = useMemo(() => {
    if (platformFilter === 'All') return hedgeExposureData;
    const keep: LegSource = platformFilter === 'MT5' ? 'MT5' : 'CTRADER';
    return hedgeExposureData.filter((r) => r.source === keep || r.source === 'LP');
  }, [hedgeExposureData, platformFilter]);

  // Risk note snapshot: all platforms, read at the moment of the request.
  const riskNoteDataRef = useRef({ legs: hedgeExposureData, exposure });
  riskNoteDataRef.current = { legs: hedgeExposureData, exposure };
  const riskNoteSnapshot = useCallback((key: string, isUnifier: boolean): RiskNoteSnapshot => {
    const { legs: all, exposure: ex } = riskNoteDataRef.current;
    const legs = all.filter((l) => (isUnifier ? l.unifierKey === key : l.groupSymbol === key));
    const members = new Set(legs.map((l) => l.groupSymbol));
    if (!isUnifier) members.add(key);
    const rows = (ex?.by_symbol ?? []).filter((r) => r && members.has(r.symbol));
    return { legs, rows, asOf: ex?.as_of ?? null };
  }, []);

  // A platform whose MASTER drops while selected falls back to All.
  useEffect(() => {
    if (platformFilter === 'MT5' && masters.mt5Up === false) setPlatformFilter('All');
    if (platformFilter === 'cTrader' && masters.ctMaster !== undefined && !masters.ctActive) setPlatformFilter('All');
  }, [platformFilter, masters.mt5Up, masters.ctMaster, masters.ctActive]);

  // Sums, Net and the per-symbol metrics are read through platformRef inside
  // the grid; re-run them, and the row styles, when the filter changes.
  useEffect(() => {
    const api = exposureGridRef.current?.api;
    if (!api) return;
    try {
      api.refreshClientSideRowModel('aggregate');
      api.refreshCells({ force: true });
      api.redrawRows();
    } catch { /* api not ready yet */ }
  }, [platformFilter]);

  // ── Header-bar stats ──────────────────────────────────────────────────────
  // All P/L is broker-perspective:
  //   • MT5 `profit` field is already broker-side (MT5 books from broker's angle)
  //   • Coverage `brokerFloatingPL` is computed broker-side in aggregator above
  // So direct sums give broker-perspective totals — no sign flips.
  //
  // Hedge metrics use per-symbol MT5-lot absolute values from the already-built
  // exposure rows, which means they share the same unit conversion (including
  // the TE contracts→lots fix) and lot-size conventions as everything else on
  // the page. That keeps the math self-consistent across instrument classes.
  const headerStats = useMemo(() => {
    // ── Position / volume counts ──────────────────────────────────────────
    // Count both books, broker-perspective:
    //   - B-Book position contributes +volume_lots with broker direction
    //     inverse of client action (`action === 'SELL'` → client sold →
    //     broker is LONG). Uses MT5 lots natively.
    //   - Coverage position contributes the LP side as placed. Needs a
    //     contracts→lots conversion (TE reports in contracts).
    // This way the Positions/Long/Short/Volume line reflects EVERY position
    // the operator is responsible for, not just the B-Book side. Orphan
    // Coverage (client closed, hedge still open) and Naked B-Book (client
    // open, no hedge) both show up in the tally.
    // Under a platform, only that platform's legs are counted; Taiga's LP
    // positions cover both platforms, so they count under All only. The
    // cTrader A-Book cover counts under cTrader, against its own clients.
    const showMt5 = platformFilter !== 'cTrader';
    const showCt  = platformFilter !== 'MT5';
    const showLp  = platformFilter === 'All';
    const bbFiltered = !showMt5 ? [] : filterServer
      ? bBookPositions.filter((p) => p.nodeName === filterServer)
      : bBookPositions;
    const ctRows  = showCt ? ct.rows : [];
    const ctBookA = ctRows.filter((r) => r.book === 'A');

    let longCount     = 0;
    let shortCount    = 0;

    // Three distinct volume concepts, each in both lots and notional:
    //   bbookLots / bbookNotional       — house inventory
    //   coverageLots / coverageNotional — LP exposure (inclusive of orphans
    //                                     — client closed but hedge still on)
    //   Net Exposure is computed from per-symbol signed signed_lots below,
    //   so that EURUSD +5 B-Book and −5 Coverage cancel on the same symbol.
    let bbookLots = 0, bbookNotional = 0;
    let coverageLots = 0, coverageNotional = 0;

    // Per-symbol signed broker-perspective lots & notional, for Net Exposure.
    // Signed: broker long = +, broker short = −. Summed across the symbol so
    // B-Book and Coverage cancel where they offset.
    const symNet = new Map<string, { lots: number; notional: number; lotSize: number }>();
    // Class-aware lot-size resolver. Prefers mt5SymbolClass (built from LP
    // mappings) so broker-specific names like 'Gold' resolve to XAU lotSize=100
    // rather than falling into the FX default. Fallback is the original
    // substring heuristic for symbols that don't have a mapping.
    const getSymLotSize = (sym: string) => {
      const cls = mt5SymbolClass.get(sym);
      const isXAU = cls?.isXAU ?? sym.includes('XAU');
      const isBTC = cls?.isBTC ?? sym.includes('BTC');
      return isXAU ? 100 : isBTC ? 1 : 100000;
    };
    const upsertSym = (sym: string, signedLots: number) => {
      const lotSize = getSymLotSize(sym);
      const cur = symNet.get(sym) ?? { lots: 0, notional: 0, lotSize };
      cur.lots     += signedLots;
      cur.notional += signedLots * lotSize;
      symNet.set(sym, cur);
    };

    for (const p of bbFiltered) {
      // MT5 `action` is the client's action; broker is the inverse.
      const brokerLong = p.action === 'SELL';
      if (brokerLong) longCount++; else shortCount++;
      const lotSize = getSymLotSize(p.symbol);
      bbookLots     += p.volume_lots;
      bbookNotional += p.volume_lots * lotSize;
      // Signed broker direction for per-symbol net exposure.
      upsertSym(p.symbol, brokerLong ? p.volume_lots : -p.volume_lots);
    }

    // cTrader clients, both books, broker view; volume is in base units.
    const ctSym = (r: CTraderPositionRow) => r.symbol || `#${r.symbol_id}`;
    for (const r of ctRows) {
      const brokerLong = r.action === 'SELL';
      if (brokerLong) longCount++; else shortCount++;
      const lots = r.volume / getSymLotSize(ctSym(r));
      bbookLots     += lots;
      bbookNotional += r.volume;
      upsertSym(ctSym(r), brokerLong ? lots : -lots);
    }
    // cTrader A-Book cover: the LP position, the client's own side.
    for (const r of ctBookA) {
      const lpLong = r.action === 'BUY';
      if (lpLong) longCount++; else shortCount++;
      const lots = r.volume / getSymLotSize(ctSym(r));
      coverageLots     += lots;
      coverageNotional += r.volume;
      upsertSym(ctSym(r), lpLong ? lots : -lots);
    }

    // Coverage positions — broker direction is the LP side as placed, so
    // `side === 'BUY'` (or long_qty > 0) means broker long on LP.
    for (const p of showLp ? aBookPositions : []) {
      if (!isOpenLpPosition(p)) continue;
      const nn = nettedNet(p);
      const isLong = nn != null ? nn > 0 : p.side === 'LONG' || p.side === 'BUY'
        || (p.long_qty > 0 && p.short_qty === 0);
      const isShort = nn != null ? nn < 0 : p.side === 'SHORT' || p.side === 'SELL'
        || (p.short_qty > 0 && p.long_qty === 0);
      if (!isLong && !isShort) continue;
      if (isLong) longCount++; else shortCount++;
      // Convert TE contracts → MT5 lots (same math as the aggregator).
      const ins = (instrByLp[p._lp ?? domLpId] ?? {})[p.symbol];
      const isXAU = p.symbol.includes('XAU');
      const isBTC = p.symbol.includes('BTC');
      const lotSize = isXAU ? 100 : isBTC ? 1 : 100000;
      const minVol  = ins?.min_trade_vol ?? (isXAU ? 10 : isBTC ? 1 : 100000);
      const qtyContracts = nn != null ? Math.abs(nn) : Math.abs(p.net_qty) || Math.max(p.long_qty, p.short_qty);
      // An LP whose quantities are contracts (LMAX) gives each one's size.
      const lots = p.contract_size ? qtyContracts * p.contract_size / lotSize
        : p.netted ? qtyContracts / lotSize : (qtyContracts * minVol) / lotSize;
      coverageLots     += lots;
      coverageNotional += lots * lotSize;
      upsertSym(p.canonical_symbol || p.symbol, isLong ? lots : -lots);
    }

    // Net Exposure — Σ |per-symbol signed net|. This is the residual
    // directional risk after B-Book and Coverage offset each other on
    // matched symbols. Perfect hedge → 0; orphans and naked positions
    // contribute their full magnitude.
    let netExposureLots = 0;
    let netExposureNotional = 0;
    for (const v of symNet.values()) {
      netExposureLots     += Math.abs(v.lots);
      netExposureNotional += Math.abs(v.notional);
    }
    // No Net under a platform: it would be one platform's clients against
    // Taiga's hedges of both.
    const netShown = showLp;
    // Under MT5 there is no coverage of MT5 alone to sum (Taiga's LP
    // positions are All platforms); under cTrader it is the cTrader A-Book.
    const coverageShown = platformFilter !== 'MT5';

    const totalLots     = bbookLots + coverageLots;
    const totalNotional = bbookNotional + coverageNotional;

    const positions = longCount + shortCount;

    // ── Float P/L — broker-perspective sum across both books ──────────────
    // MT5 `profit` is client-side (BBookPage line 78: `profit: -raw.profit`).
    // Negate to get broker's float. Coverage rows already store broker-side
    // P/L, so sum them directly.
    const bbookFloat    = bbFiltered.reduce((s, p) => s - (p.profit ?? 0), 0)
                        - ctRows.reduce((s, r) => s + ctMoney(r).clientPnl, 0);
    const coverageFloat = (showLp ? hedgeExposureData.filter((r) => r.source === 'LP') : [])
      .reduce((s, r) => s + (r.brokerFloatingPL ?? 0), 0)
      + ctBookA.reduce((s, r) => s + ctMoney(r).clientPnl, 0);
    const floatPnl = bbookFloat + coverageFloat;
    // cTrader money left out: no price or no rate to USD, or MASTER not connected.
    const ctUnpriced = ctRows.filter((r) => ctProfitUsd(r) === null).length;
    const ctStale    = ctRows.length > 0 && !masters.ctActive;

    // ── Net P/L — Float + swap + commission, both sides.
    // B-Book swap/commission follow BBookPage's convention (not negated —
    // treated as already-broker-side; see BBookPage stats.netPnL line 437).
    // Coverage swap/commission from dailyStats (backend-sourced; TE doesn't
    // populate per-position swap reliably). Best-effort until the backend
    // reports Coverage swap/commission consistently — 0 fallback on null.
    const bbookSwap = bbFiltered.reduce((s, p) => s + (p.swap ?? 0) + (p.commission ?? 0), 0)
                    + ctRows.reduce((s, r) => s + ctMoney(r).charges, 0);
    const coverageSwapComm = showLp
      ? (coverageDailyStats?.swap_net ?? 0) + (coverageDailyStats?.commission ?? 0)
      : 0;
    const netPnl = floatPnl + bbookSwap + coverageSwapComm;

    // ── Realised P/L Today — backend (Coverage) + client-side accumulator (B-Book).
    // Survives full-day close-outs because both sources persist across
    // zero-position states. B-Book side is a client-side approximation until
    // a proper MT5 closed-position endpoint exists — flagged via isBBookRealisedEstimated.
    const today            = new Date().toISOString().slice(0, 10);
    const ctDay            = ctDayStats && ctDayStats.trade_date === today ? ctDayStats : null;
    const bbookRealised    = (showMt5 ? bBookDayStats?.realized_pnl ?? 0 : 0)
                           + (showCt ? ctDay?.b_realized ?? 0 : 0);
    const coverageRealised = (showLp ? coverageDailyStats?.realized_pnl ?? 0 : 0)
                           + (showCt ? ctDay?.a_realized ?? 0 : 0);
    const realisedPnl      = bbookRealised + coverageRealised;

    // ── Hedge metrics — built from grid-row values (already broker-perspective).
    // For each symbol we need the broker-side B-Book net volume, LP-side
    // Coverage net volume, and the float P/L on each side. All four come from
    // rows the aggregators produced above, which means sign and unit are
    // already consistent with everything else on the page.
    //
    // Populates TWO outputs:
    //   1. perSymbolMetrics (Map<symbol, { hedgeRatio, hedgeEfficiency, status }>)
    //      — read by the per-symbol grid columns and also by the symbol-group
    //      roll-up valueGetters (currently pass-through since we have at most
    //      one B-Book and one Coverage row per symbol; future multi-LP would
    //      re-aggregate here).
    //   2. portfolio-level hedgedRatio for the header bar.
    type SymAgg = { bLots: number; cLots: number; bPnl: number; cPnl: number };
    const perSymbolAgg = new Map<string, SymAgg>();
    // Unifier keys carry their members' legs again: never in the portfolio sums.
    const unifierKeys = new Set<string>();
    for (const r of hedgeExposureData) {
      if (r.unifierKey) unifierKeys.add(r.unifierKey);
      // A unifier's metrics are its members' legs together: one instrument.
      if (r.unifierKey) {
        const u = perSymbolAgg.get(r.unifierKey) ?? { bLots: 0, cLots: 0, bPnl: 0, cPnl: 0 };
        if (r.isBBook) { u.bLots += r.brokerNetVol; u.bPnl += r.brokerFloatingPL ?? 0; }
        else           { u.cLots += r.brokerNetVol; u.cPnl += r.brokerFloatingPL ?? 0; }
        perSymbolAgg.set(r.unifierKey, u);
      }
      // Key on groupSymbol — the canonical mt5-side identifier — so the
      // B-Book leg and the (possibly remapped) Coverage leg of one logical
      // instrument land in the same bucket. Keying on r.symbol would split
      // them whenever an LP mapping renames the lp_symbol (e.g. TE 'XAUUSD'
      // ↔ MT5 'Gold'), and the Hedge Ratio column queried by group key
      // ('Gold') would only see the B-Book side and read 0 % coverage.
      const key = r.groupSymbol;
      const cur = perSymbolAgg.get(key) ?? { bLots: 0, cLots: 0, bPnl: 0, cPnl: 0 };
      if (r.isBBook) {
        cur.bLots += r.brokerNetVol;            // signed, broker direction
        cur.bPnl  += r.brokerFloatingPL ?? 0;   // broker-side float
      } else {
        cur.cLots += r.brokerNetVol;            // signed, LP direction
        cur.cPnl  += r.brokerFloatingPL ?? 0;
      }
      perSymbolAgg.set(key, cur);
    }

    const perSymbolMetrics = new Map<string, {
      hedgeRatio:  number | null;   // null when undefined (no B-Book)
      hedgeImpact: 'HEDGE_WORKING' | 'HEDGE_DRAG' | 'BONUS' | 'DOUBLE_LOSS' | 'FLAT' | null;
      status:      HedgeStatus;
    }>();

    // P/L threshold below which Hedge Impact stays blank — prevents the
    // four-state flag from flickering on rounding noise when the book is
    // sitting near break-even. $5 picked so a full mini-lot of spread doesn't
    // trip a state change.
    const IMPACT_FLOOR = 5.0;

    let matchedSum = 0;
    let bAbsSum    = 0;

    // Status and ratio: hedgeState (netExposure/riskNote.ts), shared with the
    // Risk note. Below LOT_EPS a side counts as none (dust after rounding).
    for (const [sym, a] of perSymbolAgg) {
      const bAbs = Math.abs(a.bLots) < LOT_EPS ? 0 : Math.abs(a.bLots);
      const inPortfolio = !unifierKeys.has(sym);
      if (inPortfolio) bAbsSum += bAbs;

      let hedgeImpact: 'HEDGE_WORKING' | 'HEDGE_DRAG' | 'BONUS' | 'DOUBLE_LOSS' | 'FLAT' | null = null;
      const { status, ratio: hedgeRatio } = hedgeState(a.bLots, a.cLots);
      if (status === 'PARTIAL' || status === 'MATCHED' || status === 'OVER' || status === 'REVERSED') {
        // Risk the cover removes: |B| − |B + C|, never below 0. Equals |C|
        // up to 100%, falls back to 0 at 200% — an over-hedge beyond twice
        // the book hedges nothing.
        const matched = Math.max(0, bAbs - Math.abs(a.bLots + a.cLots));
        if (inPortfolio) matchedSum += matched;
      }

      // ── Hedge Impact ────────────────────────────────────────────────────
      // Answers "is my hedge helping right now?" in four discrete states based
      // on the live P/L signs of each side. Blanked when either side is below
      // IMPACT_FLOOR — a nearly-flat book doesn't have a meaningful state.
      //
      //   B-Book | Coverage | Reading
      //   -------|----------|---------------------
      //   profit | profit   | BONUS         (both sides winning — hedge aligned with move)
      //   profit | loss     | HEDGE_DRAG    (broker winning, hedge eating into it — cost of insurance)
      //   loss   | profit   | HEDGE_WORKING (broker losing, hedge offsetting — what hedging is for)
      //   loss   | loss     | DOUBLE_LOSS   (both sides losing — hedge misaligned)
      //
      // Only renders on MATCHED/PARTIAL/OVER rows — on NAKED/ORPHAN/WRONG-WAY/
      // REVERSED the Status column already tells the whole story (a reversed
      // cover is a position of its own, not a hedge that is "working").
      if (status === 'MATCHED' || status === 'PARTIAL' || status === 'OVER') {
        const bSig = Math.abs(a.bPnl) < IMPACT_FLOOR ? 0 : (a.bPnl > 0 ? 1 : -1);
        const cSig = Math.abs(a.cPnl) < IMPACT_FLOOR ? 0 : (a.cPnl > 0 ? 1 : -1);
        if (bSig === 0 && cSig === 0) hedgeImpact = 'FLAT';
        else if (bSig >= 0 && cSig >= 0 && (bSig + cSig) > 0) hedgeImpact = 'BONUS';
        else if (bSig > 0 && cSig < 0)  hedgeImpact = 'HEDGE_DRAG';
        else if (bSig < 0 && cSig > 0)  hedgeImpact = 'HEDGE_WORKING';
        else if (bSig < 0 && cSig < 0)  hedgeImpact = 'DOUBLE_LOSS';
        else hedgeImpact = 'FLAT'; // mixed with one side flat — don't mislead
      }

      perSymbolMetrics.set(sym, { hedgeRatio, hedgeImpact, status });
    }

    // Portfolio Hedged Ratio = Σ risk removed / Σ |B-Book|. Only symbols with
    // active B-Book positions contribute to the denominator (orphans don't
    // make the book "less hedged" in this metric — they're flagged by status
    // in the grid instead).
    const hedgedRatio = netShown && bAbsSum > 0 ? matchedSum / bAbsSum : null;

    return {
      positions,
      longCount, shortCount,
      // Volume cells — three honest numbers:
      //   • B-Book       : house inventory
      //   • Coverage     : LP exposure (inclusive of orphans)
      //   • Net Exposure : residual directional risk after offset
      bbookLots, bbookNotional,
      coverageLots, coverageNotional,
      netExposureLots, netExposureNotional, netShown, coverageShown,
      ctUnpriced, ctStale,
      totalLots, totalNotional, // retained for any downstream consumer
      floatPnl,
      netPnl,
      realisedPnl,
      hedgedRatio,
      perSymbolMetrics,
      // Flags for UI polish
      isBBookRealisedEstimated: bbookRealised !== 0,
      hasCoverageRealised:      coverageDailyStats != null,
    };
  }, [bBookPositions, aBookPositions, instrByLp, domLpId, filterServer, hedgeExposureData, coverageDailyStats, bBookDayStats, priceTickCounter, mt5SymbolClass, platformFilter, ct.rows, ctDayStats, masters.ctActive]);

  // ── STABILIZER FOR COLUMN DEFS ───────────────────────────────────────────
  // `headerStats.perSymbolMetrics` recomputes every MD tick (~10/sec under
  // load). If the column-defs useMemo depends on `headerStats` directly, the
  // entire column-defs array identity churns on every tick, which makes AG
  // Grid tear down and rebuild every cell renderer — including the built-in
  // group-expand checkbox. That rebuild triggers a setState deep inside AG
  // Grid (setCheckboxSpacing), which triggers another component update, which
  // re-runs the column-defs memo. Infinite loop → "Maximum update depth
  // exceeded" → blank page.
  //
  // Fix: mirror perSymbolMetrics into a ref. The ref identity never changes,
  // so columns stay stable. valueGetters read from the ref's current value,
  // which updates synchronously alongside headerStats.
  const perSymbolMetricsRef = useRef(headerStats.perSymbolMetrics);
  useEffect(() => {
    perSymbolMetricsRef.current = headerStats.perSymbolMetrics;
    // Nudge AG Grid to re-run valueGetters on the three ref-backed columns.
    // This is a cell-level refresh — does NOT rebuild cell renderers or
    // trigger the checkbox re-mount that caused the render loop.
    const api = exposureGridRef.current?.api;
    if (api) {
      try {
        api.refreshCells({
          columns: ['hedgeRatio', 'hedgeImpact', 'hedgeStatus'],
          force: true,
        });
      } catch { /* api not ready yet */ }
    }
  }, [headerStats.perSymbolMetrics]);

  const activeInstrument = useMemo(
    () => instruments.find((i) => i.symbol === domSymbol) ?? null,
    [instruments, domSymbol]
  );

  const instrDecimals = activeInstrument?.price_precision
    ?? (domSymbol.includes('JPY') ? 3 : (domSymbol.includes('XAU') || domSymbol.includes('BTC')) ? 2 : 5);

  const filteredInstruments = useMemo(() => {
    const q = symbolSearch.trim().toLowerCase();
    const src = q
      ? instruments.filter((i) => i.symbol.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q))
      : instruments;
    return src.slice(0, 60);
  }, [instruments, symbolSearch]);

  const effectiveCaps = capabilities ?? {
    lp_id: domLpId, order_types: ['MARKET', 'LIMIT'], time_in_force: ['GTC', 'IOC', 'DAY'],
    max_order_qty: 10000000, min_order_qty: 1000, custom_fields: {},
  };

  const domLpInfo  = allLps.find((l) => l.lp_id === domLpId) ?? null;
  const isConnected = domLpInfo?.state === 'CONNECTED';
  // The LP's own order rules (LMAX), else the capabilities the page has.
  const lpRules = domOrderRules(domLpInfo?.provider_type, domLpId);
  const orderTypeOptions = lpRules ? lpRules.orderTypes : effectiveCaps.order_types;
  const tifOptions = lpRules ? lpRules.tifFor(domOrderType) : effectiveCaps.time_in_force;
  const orderTypeKey = orderTypeOptions.join(',');
  const tifKey = tifOptions.join(',');
  // A type or time-in-force the LP does not take gives way to its first one.
  useEffect(() => {
    if (orderTypeOptions.length && !orderTypeOptions.includes(domOrderType)) setDomOrderType(orderTypeOptions[0]);
    else if (tifOptions.length && !tifOptions.includes(domTif)) setDomTif(tifOptions[0]);
  }, [orderTypeKey, tifKey, domOrderType, domTif]); // eslint-disable-line react-hooks/exhaustive-deps
  const qtyIssue = domSymbol && !bBookSelected ? qtyProblem(domQtyLots, activeInstrument?.min_trade_vol, false) : '';
  const canBuy  = isConnected && !!domSymbol && !!domQtyLots && !qtyIssue && !submitting && !bBookSelected;
  const canSell = isConnected && !!domSymbol && !!domQtyLots && !qtyIssue && !submitting && !bBookSelected;

  // DOM Trader: a quantity the order check would refuse, shown before sending;
  // and the latest refusal (from the check, the bridge or the LP) until the
  // ticket changes or the note is clicked away.
  const [failClearedAt, setFailClearedAt] = useState(0);
  useEffect(() => { setFailClearedAt(Date.now()); }, [domQtyLots, domSymbol, domLpId]);
  const failNote = useMemo(() => {
    let last: ExecEntry | null = null;
    for (const e of execLog) if (!last || e.ts > last.ts) last = e;
    return last && last.status === 'REJECTED' && last.ts > failClearedAt ? last : null;
  }, [execLog, failClearedAt]);

  // ==========================================================================
  // GRID FIT HELPERS
  // Per product decision: columns autosize to content on initial render and
  // on structural changes (column visibility, group toggle, mode toggle,
  // viewport resize). If the grid overflows the viewport, the user
  // horizontally scrolls — we never compress columns below their content
  // width. (The previous sizeColumnsToFit fallback would truncate values
  // on narrow screens, which defeats the point.)
  //
  // Manual-resize protection: once the user drags any column header to
  // resize, fitColumns becomes a no-op for the rest of the session. The
  // flag lives in a ref (no re-render on flip) and naturally resets when
  // the component remounts on page reload — matching the "leave it alone
  // until next refresh" semantic.
  // ==========================================================================
  const userResizedRef = useRef(false);
  const fitColumns = useCallback(() => {
    if (userResizedRef.current) return;
    const api = exposureGridRef.current?.api;
    if (!api) return;
    try { api.autoSizeAllColumns(false); } catch { /* no-op */ }
  }, []);

  // Manual-resize detector. AG Grid emits onColumnResized for many sources:
  // autoSizeColumns (us), api programmatic, and uiColumnDragged (the user).
  // We only flip the flag for genuine user drags AND only on the final
  // event of a drag (event.finished === true), so dragging mid-motion
  // doesn't latch us off prematurely.
  const onColumnResized = useCallback((e: ColumnResizedEvent) => {
    if (e.finished && e.source === 'uiColumnDragged') {
      userResizedRef.current = true;
    }
  }, []);

  // Initial group expansion — all symbols COLLAPSED by default. Runs once per
  // session; user's subsequent manual expand/collapse choices are preserved
  // because getRowId keeps row identity stable across data refreshes.
  // MUST be declared before onFirstDataRendered references it (JS const TDZ).
  const hasAppliedInitialExpansionRef = useRef(false);
  const applyInitialGroupExpansion = useCallback((api: any): number => {
    // Restrict to top-level groups (level === 0) to avoid touching child nodes.
    // forEachNodeAfterFilterAndSort follows display order — consistent with
    // what the user sees on screen. Returns the number of root groups touched
    // so the caller knows whether real data had arrived yet.
    let touched = 0;
    api.forEachNodeAfterFilterAndSort((node: any) => {
      if (!node.group || node.level !== 0) return;
      node.setExpanded(false);
      touched++;
    });
    return touched;
  }, []);

  // ── Fallback: onFirstDataRendered only fires once per grid mount, and can
  // fire before data has arrived from the backend (especially on a fresh
  // page load where WS/REST are still in flight). onRowDataUpdated fires on
  // every rowData refresh, so we retry the collapse there until real groups
  // are found — then latch off via hasAppliedInitialExpansionRef.
  const onRowDataUpdated = useCallback((e: { api: any }) => {
    if (hasAppliedInitialExpansionRef.current) return;
    const touched = applyInitialGroupExpansion(e.api);
    if (touched > 0) hasAppliedInitialExpansionRef.current = true;
  }, [applyInitialGroupExpansion]);

  const onFirstDataRendered = useCallback((e: FirstDataRenderedEvent<HedgeExposureRow>) => {
    // Staggered retry sequence — initial autosize is racey because
    //   • REST bootstrap may have rendered before WS SNAPSHOT brings the
    //     full set of symbols / longer P/L strings / new columns of data.
    //   • IBM Plex Mono may not have finished loading at the moment AG Grid
    //     measures cell content — it'll measure with the fallback font and
    //     pick a width that's wrong once the real font swaps in.
    //   • AG Grid theme CSS occasionally settles a tick after the grid
    //     mounts (depends on bundling).
    // Multiple retries cheaply cover all three. fitColumns() short-circuits
    // if the user has already resized, so this never overwrites a manual
    // drag — the retries here only ever fire before the user has had a
    // chance to interact.
    requestAnimationFrame(fitColumns);
    setTimeout(fitColumns, 100);
    setTimeout(fitColumns, 300);
    setTimeout(fitColumns, 800);
    setTimeout(fitColumns, 1500);
    setTimeout(fitColumns, 3000);
    // Final pass after web fonts have actually loaded — handles the
    // measure-with-fallback-font race specifically.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(() => setTimeout(fitColumns, 50)).catch(() => { /* no-op */ });
    }
    // Only apply initial expansion once per session AND only once real groups
    // have appeared — if the first render was before data arrived, we'd mark
    // the flag prematurely and the groups would then come up in whatever
    // default state the grid chose. applyInitialGroupExpansion returns the
    // number of root groups it touched; we only flip the flag when > 0.
    if (!hasAppliedInitialExpansionRef.current) {
      const touched = applyInitialGroupExpansion(e.api);
      if (touched > 0) hasAppliedInitialExpansionRef.current = true;
    }
  }, [fitColumns, applyInitialGroupExpansion]);
  const onGridSizeChanged    = useCallback((_e: GridSizeChangedEvent<HedgeExposureRow>) => fitColumns(), [fitColumns]);
  const onColumnVisible      = useCallback((_e: ColumnVisibleEvent) => fitColumns(), [fitColumns]);
  const onColumnRowGroupChanged = useCallback((_e: ColumnRowGroupChangedEvent) => fitColumns(), [fitColumns]);

  useEffect(() => { setTimeout(fitColumns, 50); }, [fitColumns]);
  useEffect(() => { setTimeout(fitColumns, 50); }, [volumeDisplayMode, fitColumns]);

  // Symbol-set change detector — drives initial autosize (the first time
  // non-empty data appears) and re-fits when the universe of symbols changes
  // mid-session (new instrument, position fully closed). Skips trivial price-
  // tick refreshes by keying off the displayed groupSymbol set rather than the
  // hedgeExposureData reference itself, which churns on every MD tick.
  //
  // Why this is needed: onFirstDataRendered fires once per grid mount and can
  // race ahead of the WS SNAPSHOT. If REST-bootstrapped rows render first,
  // the staggered retries autosize to those (potentially empty / partial)
  // widths, and then nothing re-fits when the WS SNAPSHOT arrives with the
  // real set of symbols. This effect catches that case.
  //
  // The first-time path runs the full retry sequence (RAF + 100/300/800ms +
  // fonts.ready) — same race conditions as initial render. Subsequent
  // symbol-set changes get a single 50ms-delayed autosize, which is enough
  // because fonts/theme/etc are settled by then.
  //
  // userResizedRef is checked inside fitColumns, so once the user manually
  // resizes a column, none of these passes overwrite their drag.
  const lastSymbolSetRef = useRef<string>('');
  useEffect(() => {
    if (hedgeExposureData.length === 0) return;
    const symbolSetKey = [...new Set(hedgeExposureData.map((r) => r.groupSymbol))].sort().join('|');
    if (symbolSetKey === lastSymbolSetRef.current) return;
    const isFirstNonEmpty = lastSymbolSetRef.current === '';
    lastSymbolSetRef.current = symbolSetKey;
    if (isFirstNonEmpty) {
      requestAnimationFrame(fitColumns);
      setTimeout(fitColumns, 100);
      setTimeout(fitColumns, 300);
      setTimeout(fitColumns, 800);
      if (typeof document !== 'undefined' && document.fonts?.ready) {
        document.fonts.ready.then(() => setTimeout(fitColumns, 50)).catch(() => { /* no-op */ });
      }
    } else {
      setTimeout(fitColumns, 50);
    }
  }, [hedgeExposureData, fitColumns]);

  // Note: the previous `useEffect([hedgeExposureData])` that walked every node
  // and re-called node.setExpanded(true) has been removed. It fired on every MD
  // tick (many times per second) and was a primary cause of grid flashing.
  // With getRowId + immutable rowData diff, AG Grid preserves group expansion
  // across updates automatically; expandedGroupsRef is retained only as a hint
  // for any future session-restore / tab-switch recovery.

  // ==========================================================================
  // COLUMN DEFINITIONS
  // Layout conventions:
  //   • Header + data both LEFT-aligned (no type: rightAligned mix). Autosize
  //     calculates widths from content; centre/right-aligned headers with
  //     left-aligned cells wasted column width.
  //   • No per-column filters (no funnel icons on headers) — the page-level
  //     MT5 Node filter in the toolbar is the only filter we surface.
  //   • Numeric cells use 'font-mono' so digits align; headers stay in sans.
  //
  // Colour conventions:
  //   • Net Vol direction:  teal / amber (BBookPage convention)
  //   • Broker P/L:         muted green / muted red (branding palette)
  // ==========================================================================
  const exposureColDefs = useMemo<ColDef<HedgeExposureRow>[]>(() => {
    // Money formatter — US thousands separators, always 2 decimals:
    //   $4,567.30  /  $5,123,456.78  /  -$1,234.56
    const fmtMoney = (val: number) => {
      const abs = Math.abs(val).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${val < 0 ? '-' : ''}$${abs}`;
    };
    const fmtLots = (val: number) => `${val > 0 ? '+' : ''}${val.toFixed(2)}`;
    // Notional column shows UNITS of the base instrument (oz for XAU, EUR for
    // EURUSD, etc.), not USD value. So no $ prefix. K/M suffix kept for
    // readability on large unit counts.
    const fmtNotional = (val: number) => {
      const abs = Math.abs(val);
      if (abs >= 1_000_000) return `${val < 0 ? '-' : '+'}${(abs / 1_000_000).toFixed(2)}M`;
      if (abs >= 1_000)     return `${val < 0 ? '-' : '+'}${(abs / 1_000).toFixed(1)}K`;
      return `${val < 0 ? '-' : '+'}${abs.toFixed(2)}`;
    };
    const volColor   = (val: number) => ({ color: val > 0 ? '#49b3b3' : val < 0 ? '#e0a020' : '#999' });
    const plColor    = (val: number) => ({ color: val > 0 ? '#6aaa78' : val < 0 ? '#d07070' : '#999' });
    // Symbol-level sums (the symbol's Net) exist under All only. Under a
    // platform they would mix one platform's clients with Taiga's hedges of
    // both, so the group row shows a dash.
    const netAgg = (params: { values: unknown[] }) =>
      platformRef.current !== 'All'
        ? null
        : params.values.reduce<number>((acc, v) => acc + (Number(v) || 0), 0);
    const NET_ALL_ONLY = 'Net of the symbol: shown under All platforms only.';
    const allPlatformsLeg = (d?: HedgeExposureRow) => !!d && d.source === 'LP' && platformRef.current !== 'All';

    return [
      // Grouping driver — hidden. Drives the parent rows so B-Book and Coverage
      // legs of the same logical instrument land under one group, even when
      // they have different lp/mt5 symbol strings (e.g. 'Gold' on MT5 ↔
      // 'XAUUSD' on TraderEvolution).
      // Outer level: the unifier of a member symbol (Unifier Symbols v1.1).
      // Undefined for a stand-alone symbol, which (groupAllowUnbalanced) then
      // groups at the top level as before.
      { field: 'unifierKey', rowGroup: true, rowGroupIndex: 0, hide: true, lockVisible: true },
      { field: 'groupSymbol', rowGroup: true, rowGroupIndex: 1, hide: true, lockVisible: true },
      // Visible Symbol column — shows the leaf row's actual venue-side symbol
      // (mt5_symbol on B-Book, lp_symbol on Coverage). Group rows render blank
      // here because the parent identity is already shown in the auto-group
      // column to the left.
      // Risk note: one button per symbol / unifier row, whatever its status.
      {
        colId: 'riskNote',
        headerName: '',
        width: 96,
        minWidth: 96,
        maxWidth: 96,
        sortable: false,
        resizable: false,
        suppressAutoSize: true,
        cellRenderer: (p: { node?: { group?: boolean; key?: string | null; field?: string } }) => {
          const n = p.node;
          if (!n?.group || !n.key) return null;
          const isUnifier = n.field === 'unifierKey';
          return (
            <button
              data-risk-note="1"
              title={`Risk note for ${n.key}: a fresh analysis of the figures now`}
              onClick={(e) => { e.preventDefault(); openRiskNoteRef.current(String(n.key), isUnifier); }}
              style={{ fontSize: 11, fontWeight: 600, padding: '0 8px', lineHeight: '18px', borderRadius: 3, color: '#d2d6e2', background: '#232225', border: '1px solid #49b3b3', cursor: 'pointer' }}
            >Risk note</button>
          );
        },
      },
      { field: 'symbol', headerName: 'Symbol' },
      {
        field: 'lp', headerName: 'Liquidity Provider',
        // No [B]/[C] badges — the LP name itself carries enough context
        // (`B-Book-Ross Weiler` vs `Coverage-TraderEvolution`). Colour keeps
        // the book distinction without the extra visual noise.
        cellRenderer: (p: { value: string; data?: HedgeExposureRow }) => {
          if (!p.data) return p.value;
          return (
            <span className="inline-flex items-center gap-2">
              <span style={{ color: p.data.isBBook ? '#49b3b3' : '#c09060' }}>{p.value}</span>
              {allPlatformsLeg(p.data) && (
                <span
                  title="Taiga's LP positions cover the clients of both platforms. Same values as under All; not counted in any sum under a platform."
                  style={{ fontSize: 11, fontWeight: 600, padding: '0 6px', lineHeight: '16px', borderRadius: 3, color: '#d2d6e2', border: '1px solid #808080' }}
                >All platforms</span>
              )}
              {p.data.stale && (
                <span
                  title="The cTrader MASTER is not connected: last known positions, without prices."
                  style={{ fontSize: 11, fontWeight: 600, padding: '0 6px', lineHeight: '16px', borderRadius: 3, color: '#c09060', border: '1px solid #8a6d44' }}
                >Not live</span>
              )}
            </span>
          );
        },
      },
      { field: 'lpAccount', headerName: 'Account' },
      {
        field: volumeDisplayMode === 'Lots' ? 'brokerNetVol' : 'brokerNetNotional',
        headerName: 'Net Vol.',
        aggFunc: netAgg,
        cellClass: 'font-mono',
        valueFormatter: (p) => p.value == null
          ? (p.node?.group && platformRef.current !== 'All' ? '—' : '')
          : volumeDisplayMode === 'Lots' ? fmtLots(Number(p.value)) : fmtNotional(Number(p.value)),
        tooltipValueGetter: (p) => (p.node?.group && platformRef.current !== 'All' ? NET_ALL_ONLY : undefined),
        cellStyle: (p) => p.value != null ? volColor(Number(p.value)) : {},
      },
      {
        field: 'breakEvenPrice', headerName: 'Break-Even Px',
        aggFunc: 'avg',
        cellClass: 'font-mono',
        valueFormatter: (p) => p.value != null ? Number(p.value).toFixed(5) : '',
      },
      {
        field: 'avgPrice', headerName: 'Mkt Px',
        // Parent (group) Mkt Px: live close-out market price for the
        // aggregate net position — NOT a weighted/simple average of the
        // children's Mkt Px (which mixes BID and ASK across B-Book and
        // Coverage children and produces a meaningless mid-of-mids).
        //
        // Rule (matches the leaf logic):
        //   sum(brokerNetVol) > 0  (broker net long)  → BID
        //   sum(brokerNetVol) < 0  (broker net short) → ASK
        //   sum(brokerNetVol) = 0  (flat)             → BID by convention
        //
        // Source of BID/ASK: live FIX MD subscription in currentPricesRef,
        // keyed on the LP-side symbol. We find that LP symbol by looking
        // at a Coverage leaf child (whose `symbol` is the LP-side identifier
        // by construction — see aggregateCoverageBookPositions row build).
        // For orphan B-Book groups with no Coverage leg, currentPricesRef
        // has no entry, so we fall back to a leaf's already-resolved
        // avgPrice (which is itself BID-or-ASK aligned to that leaf's own
        // net direction via aggregateBBookPositions).
        aggFunc: (params: any) => {
          const node = params?.rowNode;
          const leaves = (node && node.allLeafChildren) || [];
          if (leaves.length === 0) return null;

          let sumNet = 0;
          let lpSymbol: string | null = null;
          for (const lf of leaves) {
            const d = lf?.data;
            if (!d) continue;
            sumNet += (d.brokerNetVol as number) || 0;
            if (lpSymbol === null && d.source === 'LP' && typeof d.symbol === 'string') {
              lpSymbol = `${d.lpId ?? ''}|${d.symbol}`;
            }
          }

          let bid: number | undefined;
          let ask: number | undefined;
          if (lpSymbol) {
            bid = currentPricesRef.current.get(lpSymbol + ':bid');
            ask = currentPricesRef.current.get(lpSymbol + ':ask');
          }

          // Pick BID/ASK from live MD per the rule.
          if (sumNet > 0) {
            if (bid != null && bid > 0) return bid;
            if (ask != null && ask > 0) return ask;   // one-sided LP feed fallback
          } else if (sumNet < 0) {
            if (ask != null && ask > 0) return ask;
            if (bid != null && bid > 0) return bid;   // one-sided LP feed fallback
          } else {
            if (bid != null && bid > 0) return bid;
            if (ask != null && ask > 0) return ask;
          }

          // No LP MD for this symbol (orphan). Pick a leaf whose own
          // direction matches the parent's net; otherwise just the first leaf.
          if (sumNet !== 0) {
            for (const lf of leaves) {
              const lfNet = (lf?.data?.brokerNetVol as number) || 0;
              if ((sumNet > 0 && lfNet > 0) || (sumNet < 0 && lfNet < 0)) {
                const v = lf?.data?.avgPrice;
                if (typeof v === 'number' && v > 0) return v;
              }
            }
          }
          for (const lf of leaves) {
            const v = lf?.data?.avgPrice;
            if (typeof v === 'number' && v > 0) return v;
          }
          return null;
        },
        cellClass: 'font-mono',
        valueFormatter: (p) => p.value != null ? Number(p.value).toFixed(5) : '',
      },
      {
        field: 'brokerFloatingPL', headerName: 'Broker P/L',
        aggFunc: netAgg,
        cellClass: 'font-mono',
        valueFormatter: (p) => {
          if (p.value == null) return p.node?.group && platformRef.current !== 'All' ? '—' : '';
          const d = p.data as HedgeExposureRow | undefined;
          return fmtMoney(Number(p.value)) + (d?.unpriced ? ' *' : '');
        },
        tooltipValueGetter: (p) => {
          if (p.node?.group && platformRef.current !== 'All') return NET_ALL_ONLY;
          const d = p.data as HedgeExposureRow | undefined;
          return d?.unpriced
            ? `${d.unpriced} position${d.unpriced === 1 ? '' : 's'} not priced (no price or no rate to USD): left out of this P/L; their volume is counted.`
            : undefined;
        },
        cellStyle: (p) => p.value != null ? plColor(Number(p.value)) : {},
      },

      // ── Hedge Ratio (per-symbol, group rows only) ────────────────────────
      // A single symbol-level metric — not a B-Book-vs-Coverage-row metric —
      // so it's blanked on leaf rows entirely. The value is the Coverage-to-
      // B-Book magnitude ratio (see headerStats.perSymbolMetrics):
      //   100%      → matched
      //   below 100 → partial / under-hedged
      //   above 100 → over-hedged (LP coverage exceeds the book)
      // Colour bands flag both directions of mismatch:
      //   95%–105% → green (effectively matched)
      //   80%–94% under or 106%–120% over → amber (slight off)
      //   anything else → red (significant)
      {
        colId: 'hedgeRatio',
        headerName: 'Hedge Ratio',
        cellClass: 'font-mono',
        valueGetter: (p) => {
          if (!p.node?.group) return null;            // leaves: no value
          if (platformRef.current !== 'All') return null;
          const sym = p.node.key as string | undefined;
          if (!sym) return null;
          const m = perSymbolMetricsRef.current.get(sym);
          return m?.hedgeRatio ?? null;
        },
        valueFormatter: (p) => {
          if (!p.node?.group)  return '';             // leaves: blank cell
          if (p.value == null) return '—';
          return `${(Number(p.value) * 100).toFixed(1)}%`;
        },
        cellStyle: (p) => {
          if (p.value == null || !p.node?.group) return {};
          const v = Number(p.value);
          if (v >= 0.95 && v <= 1.05)                              return { color: '#6aaa78' };
          if ((v >= 0.80 && v < 0.95) || (v > 1.05 && v <= 1.20))  return { color: '#c09060' };
          return { color: '#d07070' };
        },
      },

      // ── Hedge Impact (per-symbol) ────────────────────────────────────────
      // Answers "is my hedge helping right now?" in four discrete states
      // driven by the live P/L signs of each side:
      //   HEDGE WORKING  — broker losing on B-Book, hedge profiting (offset)
      //   HEDGE DRAG     — broker winning on B-Book, hedge losing (cost of insurance)
      //   BONUS          — both sides winning (hedge aligned with move)
      //   DOUBLE LOSS    — both sides losing (hedge misaligned — red flag)
      //   —              — book near flat or side without a matching peer
      // Renders only on MATCHED / PARTIAL / OVER symbols — for
      // ORPHAN / NAKED / WRONG-WAY / REVERSED the Status column tells the story.
      {
        colId: 'hedgeImpact',
        headerName: 'Hedge Impact',
        valueGetter: (p) => {
          if (platformRef.current !== 'All') return '';
          const sym = (p.node?.group ? p.node.key : p.data?.symbol) as string | undefined;
          if (!sym) return '';
          const m = perSymbolMetricsRef.current.get(sym);
          return m?.hedgeImpact ?? '';
        },
        cellRenderer: (p: { value: string; node?: { group?: boolean } }) => {
          if (!p.value || !p.node?.group) return null;
          // Palette — lifted-but-muted per branding guideline (no neon, no
          // pure saturation, "border glow" for alert states). FG bumped ~20%
          // lightness vs the previous tones; borders brightened so the badge
          // reads as a ring against the dark grid background; fontWeight 600
          // so 11px badge text doesn't get lost.
          const palette: Record<string, { c: string; bg: string; border: string; label: string }> = {
            HEDGE_WORKING: { c: '#5ed4d4', bg: '#1b2d2d', border: '#4a8a8a', label: 'HEDGE WORKING' },
            HEDGE_DRAG:    { c: '#e0b075', bg: '#2a241a', border: '#8a6d44', label: 'HEDGE DRAG'    },
            BONUS:         { c: '#80cc90', bg: '#1a2620', border: '#5a8a68', label: 'BONUS'         },
            DOUBLE_LOSS:   { c: '#e88a8a', bg: '#2a1a1d', border: '#8a4a52', label: 'DOUBLE LOSS'   },
            FLAT:          { c: '#bbb',    bg: '#1a1a1d', border: '#666',    label: '—'             },
          };
          const s = palette[p.value] ?? palette.FLAT;
          return (
            <span style={{
              fontSize: '11px', fontWeight: 600, padding: '0 6px', lineHeight: '16px',
              borderRadius: '3px',
              color: s.c, backgroundColor: s.bg, border: `1px solid ${s.border}`,
            }}>
              {s.label}
            </span>
          );
        },
      },

      // ── Hedge Status (per-symbol) ────────────────────────────────────────
      // Short-form tag surfacing the coverage state at a glance:
      //   ✓ MATCHED / PARTIAL / OVER / REVERSED / ORPHAN / NAKED / WRONG-WAY / FLAT
      //   (rules: see headerStats.perSymbolMetrics)
      {
        colId: 'hedgeStatus',
        headerName: 'Status',
        valueGetter: (p) => {
          if (platformRef.current !== 'All') return '';
          const sym = (p.node?.group ? p.node.key : p.data?.symbol) as string | undefined;
          if (!sym) return '';
          const m = perSymbolMetricsRef.current.get(sym);
          return m?.status ?? '';
        },
        tooltipValueGetter: (p) => {
          if (!p.node?.group) return undefined;
          const tips: Record<string, string> = {
            MATCHED:     'Cover offsets the book (95% to 105%).',
            PARTIAL:     'Cover offsets part of the book (below 95%).',
            OVER:        'Cover is larger than the book (105% to 200%): the net is on the other side, still smaller than the book.',
            REVERSED:    'Cover is more than twice the book: the net is on the other side and larger than with no cover at all.',
            ORPHAN:      'Cover with no book left to offset.',
            NAKED:       'Book with no cover.',
            'WRONG-WAY': 'Cover on the same side as the book: it adds to the risk.',
            FLAT:        'No book, no cover.',
          };
          return tips[String(p.value)] ?? undefined;
        },
        cellRenderer: (p: { value: string; node?: { group?: boolean } }) => {
          if (!p.value || !p.node?.group) return null;
          // Lifted-but-muted palette — kept in lock-step with the Hedge
          // Impact column above so the two tag columns share colour per state.
          const palette: Record<string, { c: string; bg: string; border: string }> = {
            MATCHED:     { c: '#80cc90', bg: '#1a2620', border: '#5a8a68' },
            PARTIAL:     { c: '#e0b075', bg: '#2a241a', border: '#8a6d44' },
            OVER:        { c: '#e0b075', bg: '#2a241a', border: '#8a6d44' },
            REVERSED:    { c: '#e88a8a', bg: '#2a1a1d', border: '#8a4a52' },
            ORPHAN:      { c: '#e88a8a', bg: '#2a1a1d', border: '#8a4a52' },
            NAKED:       { c: '#e88a8a', bg: '#2a1a1d', border: '#8a4a52' },
            'WRONG-WAY': { c: '#e88a8a', bg: '#2a1a1d', border: '#8a4a52' },
            FLAT:        { c: '#bbb',    bg: '#1a1a1d', border: '#666'    },
          };
          const s = palette[p.value] ?? palette.FLAT;
          return (
            <span style={{
              fontSize: '11px', fontWeight: 600, padding: '0 6px', lineHeight: '16px',
              borderRadius: '3px',
              color: s.c, backgroundColor: s.bg, border: `1px solid ${s.border}`,
            }}>
              {p.value === 'MATCHED' ? '✓ MATCHED' : p.value}
            </span>
          );
        },
      },
    ];
  }, [volumeDisplayMode]);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    filter: false,    // no per-column filter menus — the page toolbar has the only filter
    resizable: true,
    minWidth: 80,
    suppressHeaderMenuButton: true,  // hide the 3-dot menu next to each header
  }), []);
  const autoGroupColumnDef = useMemo<ColDef>(() => ({
    headerName: 'Symbol',
    minWidth: 150,
    cellRendererParams: { suppressCount: false },
  }), []);

  // ==========================================================================
  // GRID EVENT HANDLERS
  // ==========================================================================
  const onRowGroupOpened = useCallback((event: RowGroupOpenedEvent<HedgeExposureRow>) => {
    if (!event.node.group || !event.node.key) return;
    if (event.node.expanded) expandedGroupsRef.current.add(event.node.key);
    else expandedGroupsRef.current.delete(event.node.key);
    // The open/closed block style lives on the group row itself.
    event.api.redrawRows({ rowNodes: [event.node] });
  }, []);

  const onExposureGridReady = useCallback((_event: GridReadyEvent<HedgeExposureRow>) => {
    // Data arrives in onFirstDataRendered, not here — see that handler.
    // Kept for future use (initial column state restoration, etc).
  }, []);

  // Refs mirror the latest domSymbol and instrMap so _handleRowSelect can be
  // a stable function (no deps that change per-click). Without this, changing
  // domSymbol would churn the callback, which churns onExposureRowClicked,
  // which makes AG Grid re-wire its handler mid-click — row clicks then fire
  // intermittently depending on which render is mid-flight when you click.
  const domSymbolRef = useRef(domSymbol);
  const instrMapRef  = useRef(instrMap);
  useEffect(() => { domSymbolRef.current = domSymbol; }, [domSymbol]);
  useEffect(() => { instrMapRef.current  = instrMap;  }, [instrMap]);
  // The DOM's LP, the LP positions and each LP's instruments, for a Coverage
  // row on another LP (the DOM Trader moves to the LP that holds it).
  const domLpIdRef   = useRef(domLpId);
  const aBookRef     = useRef(aBookPositions);
  const instrByLpRef = useRef(instrByLp);
  domLpIdRef.current   = domLpId;
  aBookRef.current     = aBookPositions;
  instrByLpRef.current = instrByLp;

  /**
   * The quantity that squares a Coverage row, in the LP's own units (what the
   * order sends): the LP's net position in that symbol. A unifier member's row
   * is a share of it, so it keeps the share from the row's lots. '' when not known.
   */
  const squareQtyOf = (data: HedgeExposureRow, lp: string): string => {
    const units = (v: number) => (v > 0 ? String(Number(v.toFixed(8))) : '');
    const ins = (instrByLpRef.current[lp] ?? {})[data.symbol];
    if (!data.unifierKey) {
      let net = 0, found = false;
      for (const p of aBookRef.current) {
        if (p._lp !== lp || p.symbol !== data.symbol || !isOpenLpPosition(p)) continue;
        found = true;
        const nn = nettedNet(p);
        if (nn != null) { net += nn; continue; }
        const isShort = p.side === 'SHORT' || p.side === 'SELL' || (p.short_qty > 0 && p.long_qty === 0);
        const q = Math.abs(p.net_qty) || Math.max(p.long_qty, p.short_qty);
        net += (isShort ? -1 : 1) * q * unitsPerPositionQty(ins?.min_trade_vol, lp, 1);
      }
      if (found) return units(Math.abs(net));
    }
    const absLots = Math.abs(data.hedgeNetVol);
    return absLots > 0 ? String(Math.round(absLots * (ins?.min_trade_vol ?? 100000))) : '';
  };

  const _handleRowSelect = useCallback((data: HedgeExposureRow | null) => {
    if (data) {
      // Only Taiga's LP leg can be traded here. B-Book legs and the cTrader
      // A-Book cover (at cTrader's own LP) mute the DOM Trader.
      if (data.isBBook || data.source !== 'LP') {
        // B-Book row → mute the DOM Trader panel. These positions don't route
        // through the LP; letting the user place a FIX order here would be a
        // mistake. Clearing domSymbol tears down the book subscription too.
        setBBookSelected(true);
        if (domSymbolRef.current) { subscribedRef.current = ''; setDomSymbol(''); }
        setDomQtyLots('');
        setLimitPrice('');
      } else {
        // Coverage row → drive DOM to this symbol, un-mute and OPEN the drawer.
        setBBookSelected(false);
        setDomDrawerOpen(true);  // click-to-trade intent surfaces the panel
        // Squared at the LP that holds it: the DOM Trader moves to the row's LP.
        const rowLp = data.lpId || domLpIdRef.current;
        const lpChanged = rowLp !== domLpIdRef.current;
        if (lpChanged) {
          domLpIdRef.current = rowLp;
          setDomLpId(rowLp);
          setLiveBook(null); setBookStatus('—');
        }
        pendingPointSymRef.current = data.unifierKey ? data.groupSymbol : null;
        if (!lpChanged && data.symbol === domSymbolRef.current && data.unifierKey) {
          // Same ticket symbol: its targets are already in.
          const t = domPointTargetsRef.current.find((x) => x.symbol === data.groupSymbol);
          if (t) setDomPointTo(pointKey(t));
          pendingPointSymRef.current = null;
        }
        if (lpChanged || data.symbol !== domSymbolRef.current) {
          subscribedRef.current = ''; // force re-subscribe on new symbol
          domSymbolRef.current = data.symbol;
          setDomSymbol(data.symbol);
          setShowPicker(false);
          setLimitPrice('');
          // Pre-fill qty: what squares the row at its LP.
          setDomQtyLots(squareQtyOf(data, rowLp));
        }
      }
    }
  }, []);  // stable — refs cover all reactive reads

  const onExposureRowClicked = useCallback((event: RowClickedEvent<HedgeExposureRow>) => {
    if (onRiskNoteButton(event.event)) return;
    if (event.data) {
      _handleRowSelect(event.data);
    } else if (event.node.group && event.node.key) {
      // Explicitly toggle expansion on group-row click. Default AG Grid
      // behaviour is unreliable under the current config (getRowId + no
      // animateRows interacts with group auto-expand inconsistently), so
      // we drive it ourselves.
      event.node.setExpanded(!event.node.expanded);
    }
  }, [_handleRowSelect]);

  const onExposureCellClicked = useCallback((event: { data?: HedgeExposureRow; event?: Event | null; node?: { group?: boolean; key?: string; expanded?: boolean; setExpanded?: (v: boolean) => void } }) => {
    if (onRiskNoteButton(event.event)) return;
    if (event.data) {
      _handleRowSelect(event.data);
    } else if (event.node?.group && event.node?.key) {
      if (event.node.setExpanded) event.node.setExpanded(!event.node.expanded);
    }
  }, [_handleRowSelect]);

  // ==========================================================================
  // ORDER SUBMISSION
  // Payload shape matches CBookPage.submitOrder (which is the known-working
  // reference for /api/v1/fix/order). Key fields: qty (NOT quantity),
  // open_close: 'O' for new opens, product_type derived from symbol family.
  // ==========================================================================
  const submitOrder = useCallback(async (side: 'BUY' | 'SELL') => {
    const qty = parseFloat(domQtyLots.trim());
    if (!qty || qty <= 0 || !domLpId || !domSymbol || submitting) return;
    // An LP whose minimum is a fraction takes steps of it (LMAX: 0.1 contract).
    const minVol = activeInstrument?.min_trade_vol ?? 0;
    if (minVol > 0 && minVol < 1) {
      const nearest = Number((Math.round(qty / minVol) * minVol).toFixed(8));
      const reason = qty < minVol ? `Quantity below the minimum of ${minVol.toLocaleString()}`
        : Math.abs(nearest - qty) > 1e-9 ? `Quantity must be in steps of ${minVol.toLocaleString()} (nearest: ${nearest.toLocaleString()})`
        : '';
      if (reason) {
        setExecLog((prev) => [{
          clord_id: '—', ts: Date.now(), symbol: domSymbol, side, qty, orderType: domOrderType,
          tif: domTif, lpId: domLpId, status: 'REJECTED' as const, rejectReason: reason,
        }, ...prev].slice(0, 10));
        return;
      }
    }
    setSubmitting(true);
    const entry: ExecEntry = {
      clord_id: `pending-${Date.now()}`, ts: Date.now(),
      symbol: domSymbol, side, qty, orderType: domOrderType, tif: domTif, lpId: domLpId,
      status: 'SENT',
    };
    try {
      const body: Record<string, unknown> = {
        lp_id:         domLpId,
        symbol:        domSymbol,
        side,
        qty,                                    // C++ flat-path contract — NOT 'quantity'
        order_type:    domOrderType,
        time_in_force: domTif,
        open_close:    'O',
        product_type:  (domSymbol.includes('XAU') || domSymbol.includes('BTC') || domSymbol.includes('US') || domSymbol.includes('DE')) ? 'CFD' : 'FOREX',
      };
      if (domOrderType === 'LIMIT' || domOrderType === 'STOP') {
        const p = parseFloat(limitPrice);
        if (!p) {
          entry.status = 'REJECTED'; entry.rejectReason = `Price required for ${domOrderType}`;
          setExecLog((prev) => [entry, ...prev].slice(0, 10));
          setSubmitting(false);
          return;
        }
        body.price = p;
      }
      const target = domPointTo ? domPointTargets.find((t) => pointKey(t) === domPointTo) : undefined;
      if (target) body.point_to = { symbol: target.symbol, platform: target.platform };
      const r = await bff<FIXOrderResp>('/api/v1/fix/order', { method: 'POST', body: JSON.stringify(body) });
      if (r.success) {
        entry.clord_id = r.clord_id ?? '—';
        if (r.pointer?.symbol) entry.pointedTo = r.pointer.symbol;
        const ignored = (r.warnings ?? []).find((w) => w.code === 'POINTER_IGNORED');
        if (ignored) showDomToast(`Order placed without "Point to": ${ignored.message}`);
        // No explicit refresh needed — POSITION_REPORT on the FIX WebSocket
        // will upsert the new position into aBookPositions as soon as it lands.
      } else {
        entry.status = 'REJECTED'; entry.rejectReason = r.error ?? 'Rejected';
      }
    } catch (err) {
      entry.status = 'REJECTED';
      entry.rejectReason = err instanceof Error ? err.message : 'Request failed';
    } finally {
      setSubmitting(false);
      setExecLog((prev) => [entry, ...prev].slice(0, 10));
    }
  }, [domLpId, domSymbol, submitting, domQtyLots, domOrderType, domTif, limitPrice, domPointTo, domPointTargets, showDomToast, activeInstrument]);

  // ==========================================================================
  // DOM DISPLAY HELPERS
  // ==========================================================================
  const lpDotColor = (st: string) =>
    st === 'CONNECTED' ? '#49b3b3' : st === 'DEGRADED' ? '#e0a020'
    : (st === 'CONNECTING' || st === 'RECONNECTING') ? '#a78bfa' : '#555';

  const bookBadge = (st: string) => ({
    HEALTHY:      { text: 'LIVE',  color: '#49b3b3' },
    STALE:        { text: 'STALE', color: '#e0a020' },
    RESYNCING:    { text: 'SYNC',  color: '#a78bfa' },
    SUBSCRIBING:  { text: 'SUB…',  color: '#666'    },
    EMPTY:        { text: 'WAIT',  color: '#666'    },
    ERROR:        { text: 'ERR',   color: '#ff5c5c' },
    DISCONNECTED: { text: 'DISC',  color: '#ff5c5c' },
  }[st] ?? { text: st, color: '#555' });

  const fmtBookSize = (sz: number) =>
    sz >= 1_000_000 ? `${(sz / 1_000_000).toFixed(1)}M`
    : sz >= 100_000  ? `${(sz / 100_000).toFixed(2)}L`
    : sz >= 1_000    ? `${(sz / 1_000).toFixed(1)}K`
    : sz.toFixed(0);


  // Money formatter used in the header card — matches the grid's fmtMoney:
  //   $4,567.30  /  $5,123,456.78  /  -$1,234.56
  const fmtHdrMoney = (val: number) => {
    const abs = Math.abs(val).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${val < 0 ? '-' : ''}$${abs}`;
  };

  // Notional formatter for the header card's Volume cells.
  // Full precision with thousands separators — no K/M compaction, so a 500
  // orphan sitting alongside a 1.8M gross is never silently rounded away:
  //   500.50
  //   1,800,500
  //   5,123,456
  // Unsigned — Volume is always a magnitude, not a direction. No $ prefix:
  // these are UNITS of the base instrument (oz, EUR, etc.), not currency.
  // Decimals only appear when the value has a fractional component.
  const fmtHdrNotional = (val: number) => {
    const abs = Math.abs(val);
    const hasFraction = Math.abs(abs - Math.round(abs)) > 0.001;
    return abs.toLocaleString('en-US', {
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: 2,
    });
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: '#232326' }}>

      {/* ── Page Header — title + Overall Net Exposure card on one row ───
          Title and card sit side-by-side. On narrow viewports the card drops
          below as a unit (flex-wrap) without breaking internally.
          Card mirrors the CBook strategy-card aesthetic: teal left-border,
          dark fill, four column clusters separated by hairlines:
            1. Positions / Long·Short          — position counts
            2. B-Book Vol / Coverage Vol /     — volume breakdown
               Net Exposure
            3. Float P/L / Net P/L /           — P/L readouts
               Realised P/L
            4. Hedged Ratio                    — portfolio coverage health
          Hedge Impact and Status are per-symbol metrics in the grid below. */}
      <div className="px-4 py-2 border-b border-[#808080] flex items-center justify-between gap-4 flex-wrap">
        {/* Title — left */}
        <div>
          <h1 className="text-lg font-semibold text-white">Net Exposure</h1>
          <p className="text-xs text-[#999]">Overall Net Exposure</p>
        </div>

        {/* Overall-exposure card — right */}
        <div
          className="inline-flex items-stretch gap-4 rounded px-4 py-2 text-base"
          style={{
            backgroundColor: '#252429',
            border: '1px solid #49b3b344',
            borderLeft: '3px solid #49b3b3',
          }}
        >
          {/* GROUP 1 — positions */}
          <div className="flex items-center gap-5">
            <div>
              <div className="text-sm uppercase tracking-wider text-white mb-0.5">Positions</div>
              <div className="font-mono text-white">{headerStats.positions}</div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wider text-white mb-0.5">Long / Short</div>
              <div className="font-mono">
                <span style={{ color: '#49b3b3' }}>{headerStats.longCount}</span>
                <span className="text-[#505050]"> / </span>
                <span style={{ color: '#e0a020' }}>{headerStats.shortCount}</span>
              </div>
            </div>
          </div>

          <div className="w-px self-stretch bg-[#3a3a3e]" />

          {/* GROUP 2 — volumes
              Three honest numbers answering different questions:
                B-Book Vol   : house inventory (what you're warehousing)
                Coverage Vol : LP exposure inclusive of orphans (what's pushed out)
                Net Exposure : residual directional risk after symbol-by-symbol offset */}
          <div className="flex items-center gap-5">
            <div>
              <div className="text-sm uppercase tracking-wider text-white mb-0.5">B-Book Vol</div>
              <div className="font-mono text-white">
                {volumeDisplayMode === 'Lots'
                  ? `${headerStats.bbookLots.toFixed(2)} lots`
                  : fmtHdrNotional(headerStats.bbookNotional)}
              </div>
            </div>
            <div title={!headerStats.coverageShown
                ? "Taiga's LP positions cover the clients of both platforms: shown under All."
                : platformFilter === 'cTrader' ? 'The cTrader A-Book: book A positions at cTrader\'s own LP.' : undefined}>
              <div className="text-sm uppercase tracking-wider text-white mb-0.5">Coverage Vol</div>
              <div className="font-mono text-white">
                {!headerStats.coverageShown ? '—'
                  : volumeDisplayMode === 'Lots'
                  ? `${headerStats.coverageLots.toFixed(2)} lots`
                  : fmtHdrNotional(headerStats.coverageNotional)}
              </div>
            </div>
            <div title={headerStats.netShown
                ? 'Residual directional risk after B-Book and Coverage net out per symbol. Perfect hedge = 0; orphan and naked positions contribute their full magnitude.'
                : "Shown under All only: Taiga's LP positions cover both platforms, so a net of one platform would be wrong."}>
              <div className="text-sm uppercase tracking-wider text-white mb-0.5">Net Exposure</div>
              <div className="font-mono text-white">
                {!headerStats.netShown ? '—'
                  : volumeDisplayMode === 'Lots'
                  ? `${headerStats.netExposureLots.toFixed(2)} lots`
                  : fmtHdrNotional(headerStats.netExposureNotional)}
              </div>
            </div>
          </div>

          <div className="w-px self-stretch bg-[#3a3a3e]" />

          {/* GROUP 3 — P/L */}
          <div className="flex items-center gap-5">
            <div>
              <div className="text-sm uppercase tracking-wider text-white mb-0.5">Float P/L</div>
              <div className="font-mono" style={{
                color: headerStats.floatPnl > 0 ? '#6aaa78' : headerStats.floatPnl < 0 ? '#d07070' : '#d2d6e2',
              }}>
                {fmtHdrMoney(headerStats.floatPnl)}
                {(headerStats.ctUnpriced > 0 || headerStats.ctStale) && (
                  <span className="cursor-help ml-1" style={{ color: '#c09060' }}
                    title={headerStats.ctStale
                      ? 'The cTrader MASTER is not connected: its positions are the last known, without prices.'
                      : `${headerStats.ctUnpriced} cTrader position${headerStats.ctUnpriced === 1 ? '' : 's'} not priced (no price or no rate to USD): left out of this figure.`}>*</span>
                )}
              </div>
            </div>
            <div>
              <div className="text-sm uppercase tracking-wider text-white mb-0.5">Net P/L</div>
              <div className="font-mono" style={{
                color: headerStats.netPnl > 0 ? '#6aaa78' : headerStats.netPnl < 0 ? '#d07070' : '#d2d6e2',
              }}>
                {fmtHdrMoney(headerStats.netPnl)}
              </div>
            </div>
            <div title="Today. Taiga LP side from the bridge's daily figures; MT5 and cTrader client side, and the cTrader A-Book, added up on this page from the positions closed while it is open.">
              <div className="text-sm uppercase tracking-wider text-white mb-0.5">Realised P/L</div>
              <div className="font-mono" style={{
                color: headerStats.realisedPnl > 0 ? '#6aaa78' : headerStats.realisedPnl < 0 ? '#d07070' : '#d2d6e2',
              }}>
                {fmtHdrMoney(headerStats.realisedPnl)}
              </div>
            </div>
          </div>

          <div className="w-px self-stretch bg-[#3a3a3e]" />

          {/* GROUP 3 — portfolio hedge coverage */}
          <div className="flex items-center gap-5">
            <div title={headerStats.netShown
                ? 'Portfolio Hedged Ratio: client-direction lots covered by an opposite-direction hedge, summed across symbols. 100% = every B-Book lot has a matching Coverage lot in the opposite direction. Per-symbol breakdown in the grid below.'
                : "Shown under All only: Taiga's LP positions cover both platforms."}>
              <div className="text-sm uppercase tracking-wider text-white mb-0.5">Hedged Ratio</div>
              {headerStats.hedgedRatio != null ? (
                <div className="font-mono" style={{
                  color: headerStats.hedgedRatio >= 0.95 ? '#6aaa78'
                    : headerStats.hedgedRatio >= 0.8  ? '#c09060' : '#d07070',
                }}>
                  {(headerStats.hedgedRatio * 100).toFixed(1)}%
                </div>
              ) : (
                <div className="font-mono text-[#666]">—</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ───────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden px-2 pt-2 pb-12">

        {/* Filter bar — matches BBookPage pattern (strip across top, not scattered chips) */}
        <div className="px-2 py-1.5 mb-2 border-b border-[#505050] flex items-center gap-4 flex-wrap" style={{ backgroundColor: '#2a292c' }}>
          <span className="text-[10px] text-[#666] uppercase tracking-wider font-medium">Filters</span>

          {/* Platform — only the two MASTER nodes count. A platform whose
              master is not connected cannot be chosen. */}
          <div className="flex items-center gap-1.5">
            <label htmlFor="netexp-platform" className="text-xs text-[#d2d6e2]">Platform</label>
            <select
              id="netexp-platform"
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value as PlatformFilter)}
              aria-label="Platform"
              className="w-[190px] bg-[#232225] border border-[#606060] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#49b3b3]"
            >
              <option value="All">All</option>
              <option value="MT5" disabled={!masters.mt5Active}>{masters.mt5Active ? 'MT5' : 'MT5 (not connected)'}</option>
              <option value="cTrader" disabled={!masters.ctActive}>{masters.ctActive ? 'cTrader' : 'cTrader (not connected)'}</option>
            </select>
            {platformFilter !== 'All' && (
              <span className="text-xs text-[#d2d6e2]"
                title="Taiga's LP positions cover the clients of both platforms. They stay listed, greyed and tagged All platforms, with the same values as under All, and are left out of every sum.">
                Net and hedge status: All only
              </span>
            )}
          </div>

          <div className="h-4 w-px bg-[#505050]" />

          {/* Lots / Notional toggle */}
          <div className="flex items-center gap-2">
            <span className={clsx('text-xs transition-colors', volumeDisplayMode === 'Lots' ? 'text-white' : 'text-[#666]')}>Lots</span>
            <button
              onClick={() => setVolumeDisplayMode(volumeDisplayMode === 'Lots' ? 'Notional' : 'Lots')}
              className={clsx('relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out p-0.5',
                volumeDisplayMode === 'Notional' ? 'bg-[#49b3b3]' : 'bg-[#606060]')}
            >
              <span className={clsx('block w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 ease-in-out',
                volumeDisplayMode === 'Notional' ? 'translate-x-5' : 'translate-x-0')} />
            </button>
            <span className={clsx('text-xs transition-colors', volumeDisplayMode === 'Notional' ? 'text-white' : 'text-[#666]')}>Notional</span>
          </div>

          <div className="h-4 w-px bg-[#505050]" />

          {/* Group expand controls — grouped at the right so they don't visually compete with filters */}
          <button onClick={() => exposureGridRef.current?.api?.expandAll()}
            className="px-3 py-1 text-xs text-[#999] hover:text-white border border-[#606060] hover:border-[#808080] rounded transition-colors">
            Expand All
          </button>
          <button onClick={() => exposureGridRef.current?.api?.collapseAll()}
            className="px-3 py-1 text-xs text-[#999] hover:text-white border border-[#606060] hover:border-[#808080] rounded transition-colors">
            Collapse All
          </button>
        </div>

        {/* ── Main content area ──────────────────────────────────── */}
        <div className="flex-1 flex overflow-hidden gap-2 min-h-0">

          {/* ── Risk note drawer (left; DOM Trader keeps the right) ── */}
          <RiskNoteDrawer request={riskNoteReq} snapshot={riskNoteSnapshot} onClose={() => setRiskNoteReq(null)} />

          {/* ── Left column: Grid ────────────────────────────────── */}
          <div className="flex-1 flex flex-col min-h-0 min-w-0">

            {/* Exposure grid */}
            <div className="flex-1 min-h-0">
              <AgGridReact<HedgeExposureRow>
                ref={exposureGridRef}
                theme={gridTheme}
                rowData={gridRows}
                columnDefs={exposureColDefs}
                defaultColDef={defaultColDef}
                autoGroupColumnDef={autoGroupColumnDef}
                // Size each column to its widest cell + header, then leave
                // any leftover viewport space empty on the right. Without
                // this, AG Grid's default behaviour distributes leftover
                // width across columns ("spreads"), which on a 27"/32"
                // monitor turns 1.5k pixels of content into 2.5k pixels of
                // padded-out columns. fitColumns() also calls
                // autoSizeAllColumns later for late-arriving data and
                // post-mode-toggle re-fits — both produce the same
                // content-fit result, so the strategy and the retries
                // stay consistent.
                autoSizeStrategy={{ type: 'fitCellContents' }}
                groupDefaultExpanded={0}
                groupAllowUnbalanced={true}
                suppressAggFuncInHeader={true}
                // Stable row identity — AG Grid does an immutable diff on rowData
                // change, updating only the cells whose values actually changed,
                // instead of tearing down and rebuilding rows on every price tick.
                // Without this, the grid visibly flashes and jumps on every MD tick.
                getRowId={(params) => (params.data as HedgeExposureRow).id}
                // Cell-change flash is disabled via suppressCellFlashing on individual
                // column defs where needed; v35 doesn't accept enableCellChangeFlash
                // at the grid level any more.
                // Do NOT re-measure columns or re-apply animations on data refresh.
                animateRows={false}
                rowHeight={28}
                headerHeight={36}
                // Expanded symbol = one block: the open group row is lifted
                // and railed in teal, its rows share one darker tint and the
                // same rail, and the last row closes the block. Collapsed
                // groups keep the plain grid. B-Book vs Coverage stays on the
                // Liquidity Provider colour (teal / amber).
                getRowStyle={(params) => {
                  const node = params.node;
                  if (node.group && node.expanded) {
                    return { backgroundColor: '#263234', borderLeft: '3px solid #49b3b3', fontWeight: 600 };
                  }
                  const parent = node.parent;
                  if (!parent || !parent.group || !parent.expanded) return undefined;
                  const style: Record<string, string | number> = {
                    backgroundColor: '#1f2627', borderLeft: '3px solid #3a6e6e',
                  };
                  if (node.lastChild) style.borderBottom = '1px solid #3a6e6e';
                  if (params.data?.isBBook) style.cursor = 'default';
                  // Taiga's LP leg under a platform: greyed, outside every sum.
                  if (params.data && params.data.source === 'LP' && platformRef.current !== 'All') {
                    style.opacity = 0.55;
                    style.borderLeft = '3px dashed #808080';
                  }
                  return style;
                }}
                onRowGroupOpened={onRowGroupOpened}
                onGridReady={onExposureGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataUpdated={onRowDataUpdated}
                onGridSizeChanged={onGridSizeChanged}
                onColumnVisible={onColumnVisible}
                onColumnResized={onColumnResized}
                onColumnRowGroupChanged={onColumnRowGroupChanged}
                onRowClicked={onExposureRowClicked}
                onCellClicked={onExposureCellClicked}
                rowSelection={{ mode: 'singleRow', enableClickSelection: true }}
              />
            </div>
          </div>

          {/* DOM Trader panel — gated on dom_trader >= VIEW. NONE hides it; the
              page stays visible as read-only monitoring. BFF enforces the same. */}
          {hasPermission('dom_trader', 'VIEW') && (<>
          {/* ── DOM Trader Drawer ────────────────────────────────────────
              Default: collapsed 32px rail with an expand chevron. Click the
              rail → opens the full panel. Inside the open panel, the header
              has a close chevron (›) on the far right.
              Clicking a Coverage row in the grid also auto-opens the drawer
              (see _handleRowSelect).
              The panel subtree stays mounted in both states so order form
              state (qty, limit, exec log, WS subscriptions) survives the
              open/close cycle — we only hide the DOM elements, not unmount. */}
          {!domDrawerOpen && (
            <button
              onClick={() => setDomDrawerOpen(true)}
              className="flex flex-col items-center justify-center flex-shrink-0 border border-[#555] rounded transition-colors hover:bg-[#2a292c]"
              style={{ width: '32px', backgroundColor: '#232225' }}
              title="Open DOM Trader"
            >
              <span style={{ color: '#49b3b3', fontSize: '18px', lineHeight: '1' }}>‹</span>
              <span
                className="text-[10px] text-[#999] uppercase tracking-wider mt-2"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: '0.12em' }}
              >
                DOM Trader
              </span>
            </button>
          )}

          {/* ── DOM Trader Panel (real FIX, identical to CBookPage) ── */}
          <div
            className="relative flex flex-col border border-[#555] rounded overflow-hidden flex-shrink-0 transition-opacity"
            style={{
              width: '390px',
              backgroundColor: '#232225',
              // Visually mute the whole panel when a B-Book row is selected.
              // BUY/SELL buttons are also hard-gated via canBuy/canSell above.
              opacity: bBookSelected ? 0.45 : 1,
              // When the drawer is closed, hide the panel entirely — `display:
              // none` so it takes zero width/height but its state + mounted
              // subtree survive (WS, exec log, form values).
              display: domDrawerOpen ? 'flex' : 'none',
            }}
          >

            {/* Panel header */}
            <div className="px-3 py-2 border-b border-[#555] flex items-center justify-between flex-shrink-0" style={{ backgroundColor: '#1a1a1c' }}>
              <span className="text-base font-medium text-white">Market Depth</span>
              <div className="flex items-center gap-2">
                {bBookSelected ? (
                  <span
                    className="text-sm font-mono px-1.5 py-0.5 rounded"
                    style={{ color: '#c09060', backgroundColor: '#2a2016', border: '1px solid #c09060' }}
                    title="B-Book positions don't route through the LP — DOM trading is disabled for this selection."
                  >
                    B-BOOK — DISABLED
                  </span>
                ) : bookStatus !== '—' && (() => {
                  const b = bookBadge(bookStatus);
                  return (
                    <span className="text-sm font-mono px-1.5 py-0.5 rounded"
                      style={{ color: b.color, backgroundColor: `${b.color}18`, border: `1px solid ${b.color}44` }}>
                      {b.text}
                    </span>
                  );
                })()}
                <div className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: domLpInfo ? lpDotColor(domLpInfo.state) : '#555' }}
                  title={domLpInfo ? `${domLpId}: ${domLpInfo.state}` : 'No LP'} />
                {/* Close-drawer chevron. Clicking collapses the panel back
                    to the 32px rail. Form state survives the transition. */}
                <button
                  onClick={() => setDomDrawerOpen(false)}
                  className="text-[#999] hover:text-white transition-colors ml-1"
                  style={{ fontSize: '20px', lineHeight: '1' }}
                  title="Collapse DOM Trader"
                >
                  ›
                </button>
              </div>
            </div>

            {/* LP session status */}
            {lpStatus && (
              <div className="px-3 py-1 border-b border-[#333] flex items-center gap-3 text-sm flex-shrink-0" style={{ backgroundColor: '#191a1c' }}>
                <span className="text-white">Trading:</span>
                <span style={{ color: lpStatus.trading_session.state === 'LOGGED_ON' ? '#49b3b3' : '#e0a020' }}>{lpStatus.trading_session.state}</span>
                <span className="text-white">MD:</span>
                <span style={{ color: lpStatus.md_session.state === 'LOGGED_ON' ? '#49b3b3' : '#e0a020' }}>{lpStatus.md_session.state}</span>
              </div>
            )}

            {/* LP Selector */}
            <div className="px-3 py-2 border-b border-[#555] flex-shrink-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-white w-5 flex-shrink-0">LP</span>
                <select
                  value={domLpId}
                  onChange={(e) => {
                    setDomLpId(e.target.value);
                    setDomSymbol(''); setLiveBook(null); setBookStatus('—'); subscribedRef.current = '';
                  }}
                  className="flex-1 bg-[#2a2a2c] border border-[#555] rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-[#49b3b3] min-w-0"
                >
                  <option value="">— Select LP —</option>
                  {allLps.filter((l) => isListedLp(l.lp_id, domLpId)).map((l) => (
                    <option key={l.lp_id} value={l.lp_id}>
                      {l.lp_name ?? l.lp_id}{l.state !== 'CONNECTED' ? ` (${l.state})` : ''}
                    </option>
                  ))}
                </select>
                {domLpInfo && (
                  <span className="text-xs font-mono flex-shrink-0 px-1 py-0.5 rounded"
                    style={{ color: lpDotColor(domLpInfo.state), backgroundColor: `${lpDotColor(domLpInfo.state)}18`, border: `1px solid ${lpDotColor(domLpInfo.state)}33` }}>
                    {domLpInfo.state}
                  </span>
                )}
              </div>
            </div>

            {/* Symbol Selector */}
            <div className="px-3 py-2 border-b border-[#555] flex-shrink-0">
              <div className="flex items-center gap-1.5">
                <span className="text-white w-5 flex-shrink-0" style={{ fontSize: 9 }}>SYM</span>
                <button
                  onClick={() => { if (domLpId) setShowPicker((v) => !v); }}
                  disabled={!domLpId || instrLoading}
                  className={clsx(
                    'flex-1 flex items-center justify-between bg-[#2a2a2c] border rounded px-2 py-1 text-sm transition-colors min-w-0',
                    domSymbol ? 'text-white border-[#49b3b3]' : 'text-[#666] border-[#555]',
                    (!domLpId || instrLoading) && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <span className="font-mono font-semibold truncate">
                    {domSymbol || (instrLoading ? 'Loading…' : domLpId ? 'Select symbol…' : '—')}
                  </span>
                  <span className="text-[#444] ml-1 flex-shrink-0">{showPicker ? '▲' : '▼'}</span>
                </button>
              </div>
              {showPicker && (
                <div className="mt-1.5">
                  <input
                    type="text" autoFocus
                    value={symbolSearch}
                    onChange={(e) => setSymbolSearch(e.target.value)}
                    placeholder="Search symbol or description…"
                    className="w-full bg-[#1a1a1c] border border-[#49b3b3] rounded px-2 py-1.5 text-sm text-white placeholder-[#444] focus:outline-none mb-1"
                  />
                  <div className="border border-[#444] rounded overflow-y-auto" style={{ maxHeight: '160px', backgroundColor: '#1a1a1c' }}>
                    {filteredInstruments.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-[#444]">No symbols found</div>
                    ) : filteredInstruments.map((ins) => (
                      <button
                        key={ins.symbol}
                        onClick={() => {
                          subscribedRef.current = '';
                          setDomSymbol(ins.symbol);
                          setShowPicker(false); setSymbolSearch('');
                          setLimitPrice('');
                        }}
                        className="w-full flex items-center justify-between px-3 py-1 text-sm hover:bg-[#2a2a2c] transition-colors text-left"
                      >
                        <span className="font-mono font-semibold text-white">{ins.symbol}</span>
                        <div className="text-right ml-2">
                          {ins.instrument_group && <span className="text-[#555] text-xs mr-1">{ins.instrument_group}</span>}
                          <span className="text-[#444] text-xs">{ins.currency}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {domPointTargets.length > 0 && (
                <div className="flex items-center gap-1.5 mt-1.5" title="The unifier member this order is for. Reporting only: the order trades the same either way.">
                  <span className="text-white flex-shrink-0" style={{ fontSize: 9, width: 20 }}>TO</span>
                  <select
                    value={domPointTo}
                    onChange={(e) => {
                      setDomPointTo(e.target.value);
                      writePointStore(`${domLpId}|${domSymbol}`, e.target.value);
                    }}
                    className="flex-1 bg-[#2a2a2c] border border-[#555] rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-[#49b3b3] min-w-0"
                    aria-label="Point to"
                  >
                    <option value="">Point to: None</option>
                    {domPointTargets.map((t) => (
                      <option key={pointKey(t)} value={pointKey(t)}>{t.symbol} ({t.unifier})</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Best Bid / Ask */}
            <div className="px-3 py-1.5 border-b border-[#555] flex-shrink-0">
              {liveBook && liveBook.best_bid != null ? (
                <div className="grid grid-cols-3 text-xs">
                  <div>
                    <div className="text-white mb-0.5">Best Bid</div>
                    <BigFigurePrice price={liveBook.best_bid} precision={instrDecimals} accent="#4ecdc4" handlePx={14} pipPx={20} pipettePx={11} />
                  </div>
                  <div className="text-center">
                    <div className="text-white mb-0.5">Spread</div>
                    <div className="font-mono text-white">{liveBook.spread != null ? liveBook.spread.toFixed(instrDecimals) : '—'}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-white mb-0.5">Best Ask</div>
                    {liveBook.best_ask != null
                      ? <BigFigurePrice price={liveBook.best_ask} precision={instrDecimals} accent="#ff6b6b" handlePx={14} pipPx={20} pipettePx={11} />
                      : <span className="font-mono font-bold text-[#666]">—</span>}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-[#444] text-center py-0.5">
                  {bBookSelected ? 'B-Book selected — DOM Trader disabled (not routed through LP)'
                    : !domLpId ? 'Select LP above' : !domSymbol ? 'Select symbol or click a row'
                    : bookStatus === 'SUBSCRIBING' ? 'Subscribing…'
                    : bookStatus === 'EMPTY' ? 'Subscribed — awaiting snapshot'
                    : bookStatus === 'DISCONNECTED' ? 'Session disconnected'
                    : 'Awaiting market data'}
                </div>
              )}
            </div>

            {/* Order Book (5 levels) */}
            <div className="px-2 py-1 border-b border-[#555] flex-shrink-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white">
                    <th className="text-right py-0.5 pr-1.5 font-normal text-xs">Size</th>
                    <th className="text-center py-0.5 font-normal text-xs">Bid</th>
                    <th className="text-center py-0.5 font-normal text-xs">Ask</th>
                    <th className="text-left py-0.5 pl-1.5 font-normal text-xs">Size</th>
                  </tr>
                </thead>
                <tbody>
                  {liveBook && liveBook.bids.length > 0 ? (() => {
                    const allSizes = [...liveBook.bids.map((b) => b.size), ...liveBook.asks.map((a) => a.size)];
                    const maxSz = Math.max(...allSizes, 1);
                    return Array.from({ length: DOM_DEPTH }).map((_, i) => {
                      const b = liveBook.bids[i]; const a = liveBook.asks[i];
                      return (
                        <tr key={i}>
                          <td className="text-right py-0.5 pr-1.5 relative">
                            {b && <div className="absolute right-0 top-0 bottom-0 opacity-20 rounded-l" style={{ width: `${(b.size / maxSz) * 100}%`, backgroundColor: '#4ecdc4' }} />}
                            <span className="relative font-mono text-[13px]" style={{ color: b ? '#4ecdc4' : '#2a2a2a' }}>{b ? fmtBookSize(b.size) : '—'}</span>
                          </td>
                          <td className="text-center py-0.5">
                            {b
                              ? <BigFigurePrice price={b.price} precision={instrDecimals} accent="#4ecdc4" handlePx={12} pipPx={17} pipettePx={10} />
                              : <span className="font-mono text-[13px]" style={{ color: '#2a2a2a' }}>—</span>}
                          </td>
                          <td className="text-center py-0.5">
                            {a
                              ? <BigFigurePrice price={a.price} precision={instrDecimals} accent="#ff6b6b" handlePx={12} pipPx={17} pipettePx={10} />
                              : <span className="font-mono text-[13px]" style={{ color: '#2a2a2a' }}>—</span>}
                          </td>
                          <td className="text-left py-0.5 pl-1.5 relative">
                            {a && <div className="absolute left-0 top-0 bottom-0 opacity-20 rounded-r" style={{ width: `${(a.size / maxSz) * 100}%`, backgroundColor: '#ff6b6b' }} />}
                            <span className="relative font-mono text-[13px]" style={{ color: a ? '#ff6b6b' : '#2a2a2a' }}>{a ? fmtBookSize(a.size) : '—'}</span>
                          </td>
                        </tr>
                      );
                    });
                  })() : (
                    Array.from({ length: DOM_DEPTH }).map((_, i) => (
                      <tr key={i}>
                        <td className="text-right py-0.5 pr-1.5 font-mono text-[13px] text-[#2a2a2a]">—</td>
                        <td className="text-center py-0.5 font-mono text-[13px] text-[#2a2a2a]">—</td>
                        <td className="text-center py-0.5 font-mono text-[13px] text-[#2a2a2a]">—</td>
                        <td className="text-left py-0.5 pl-1.5 font-mono text-[13px] text-[#2a2a2a]">—</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Order Entry */}
            <div className="px-3 py-2 border-b border-[#555] flex-shrink-0">
              {/* Type + TIF */}
              <div className="flex gap-2 mb-2">
                <select
                  value={domOrderType}
                  onChange={(e) => setDomOrderType(e.target.value)}
                  className="flex-1 bg-[#2a2a2c] border border-[#555] rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-[#49b3b3]"
                >
                  {orderTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select
                  value={domTif}
                  onChange={(e) => setDomTif(e.target.value)}
                  className="flex-1 bg-[#2a2a2c] border border-[#555] rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-[#49b3b3]"
                >
                  {tifOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              {/* Qty */}
              <div className="mb-2">
                <div className="text-xs text-white mb-1">QTY{lpRules ? ` (${lpRules.qtyUnit})` : ''}</div>
                <input
                  type="number" min="0"
                  step={activeInstrument && activeInstrument.min_trade_vol > 0 && activeInstrument.min_trade_vol < 1 ? activeInstrument.min_trade_vol : undefined}
                  value={domQtyLots}
                  onChange={(e) => setDomQtyLots(e.target.value)}
                  disabled={!domSymbol}
                  placeholder={lpRules && activeInstrument ? activeInstrument.min_trade_vol.toLocaleString()
                    : effectiveCaps.min_order_qty != null ? effectiveCaps.min_order_qty.toLocaleString() : '0'}
                  className={clsx(
                    'w-full bg-[#2a2a2c] border border-[#555] rounded px-2 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-[#49b3b3]',
                    !domSymbol && 'opacity-50 cursor-not-allowed'
                  )}
                />
              </div>

              {/* Limit price (only shown for LIMIT / STOP) */}
              {(domOrderType === 'LIMIT' || domOrderType === 'STOP') && (
                <div className="mb-2">
                  <div className="text-xs text-white mb-1">PRICE</div>
                  <input
                    type="number" min="0" step="0.00001"
                    value={limitPrice}
                    onChange={(e) => setLimitPrice(e.target.value)}
                    disabled={!domSymbol}
                    placeholder="0.00000"
                    className={clsx(
                      'w-full bg-[#2a2a2c] border border-[#555] rounded px-2 py-1.5 text-sm text-white font-mono focus:outline-none focus:border-[#49b3b3]',
                      !domSymbol && 'opacity-50 cursor-not-allowed'
                    )}
                  />
                </div>
              )}

              {/* BUY / SELL */}
              <div className="flex gap-2">
                <button
                  onClick={() => submitOrder('BUY')}
                  disabled={!canBuy}
                  className={clsx(
                    'flex-1 py-2 rounded text-sm font-semibold transition-colors',
                    canBuy ? 'bg-[#49b3b3] hover:bg-[#3dbdb5] text-black' : 'bg-[#2a2a2c] text-[#444] cursor-not-allowed border border-[#555]'
                  )}
                >
                  {submitting ? '…' : 'BUY'}
                </button>
                <button
                  onClick={() => submitOrder('SELL')}
                  disabled={!canSell}
                  className={clsx(
                    'flex-1 py-2 rounded text-sm font-semibold transition-colors',
                    canSell ? 'bg-[#e0a020] hover:bg-[#c89018] text-black' : 'bg-[#2a2a2c] text-[#444] cursor-not-allowed border border-[#555]'
                  )}
                >
                  {submitting ? '…' : 'SELL'}
                </button>
              </div>
              {/* Why the order cannot go, or why the last one failed */}
              {qtyIssue ? (
                <div role="alert" className="mt-2 px-2 py-1.5 rounded text-[11px]"
                  style={{ color: '#e0a020', backgroundColor: '#2a2210', border: '1px solid #e0a02044' }}>
                  ⚠ {qtyIssue}
                </div>
              ) : failNote ? (
                <div role="alert" className="mt-2 px-2 py-1.5 rounded text-[11px] cursor-pointer"
                  onClick={() => setFailClearedAt(Date.now())}
                  style={{ color: '#ff7070', backgroundColor: '#2a1414', border: '1px solid #ff5c5c44' }}>
                  ✕ Failed: {failNote.rejectReason || 'Rejected'}
                </div>
              ) : null}
            </div>

            {/* Exec Log */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="px-3 py-1.5 border-b border-[#333] flex items-center justify-between" style={{ backgroundColor: '#1a1a1c' }}>
                <span className="text-xs uppercase tracking-wider text-white">Order Log</span>
                {execLog.length > 0 && (
                  <button onClick={() => setExecLog([])} className="text-xs text-[#666] hover:text-[#aaa] transition-colors">clear</button>
                )}
              </div>
              {execLog.length === 0 ? (
                <div className="px-3 py-3 text-sm text-[#555]">
                  {!domLpId ? 'Select an LP to begin' : !domSymbol ? 'Select a symbol or click a row' : 'No orders this session'}
                </div>
              ) : execLog.map((e, idx) => (
                <div key={`${e.clord_id}-${idx}`} className="px-3 py-2 border-b border-[#222]">
                  <div className="flex items-center justify-between mb-0.5">
                    <div className="flex items-center gap-1.5 text-sm">
                      <span className="font-bold" style={{ color: e.side === 'BUY' ? '#49b3b3' : '#e0a020' }}>{e.side}</span>
                      <span className="font-mono text-white font-semibold">{e.symbol}</span>
                      <span className="font-mono text-white">{e.qty.toLocaleString()}</span>
                      {e.pointedTo && <span className="font-mono text-white" title="Pointed to">→ {e.pointedTo}</span>}
                    </div>
                    <span className="text-xs font-bold font-mono" style={{ color: e.status === 'SENT' ? '#49b3b3' : '#ff5c5c' }}>{e.status}</span>
                  </div>
                  <div className="text-xs font-mono text-[#aaa] truncate">{e.clord_id}</div>
                  {e.rejectReason && <div className="text-xs text-[#ff5c5c] mt-0.5">{e.rejectReason}</div>}
                  <div className="text-xs text-[#666] mt-0.5">{new Date(e.ts).toLocaleTimeString()} · {e.orderType}/{e.tif}</div>
                </div>
              ))}
            </div>

            {domToast && (
              <div
                role="status"
                onClick={() => setDomToast(null)}
                className="absolute left-3 right-3 bottom-3 z-20 rounded px-3 py-2 text-sm text-white cursor-pointer"
                style={{ backgroundColor: '#2a292c', border: '1px solid #c09060' }}
              >
                {domToast}
              </div>
            )}
          </div>{/* end DOM panel */}
          </>)}

        </div>
      </div>
    </div>
  );
}

export default NetExposurePage;